import React, { useMemo, useState, useRef, useEffect, startTransition } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  TextField,
  Checkbox,
  ListItemIcon,
  OutlinedInput,
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Error,
  PlayArrow,
  Schedule,
  Assessment,
  Warning,
  PictureAsPdf,
  Close,
  FilterList,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { DASHBOARD_STATS_QUERY, API_EVALUATION_SUMMARY_QUERY } from '../graphql/dashboard';
import { CREATE_COMPLIANCE_REPORT, MARK_REPORT_EXPORTED, ComplianceReport } from '../graphql/compliance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AccessibleTooltip } from '../components/AccessibleTooltip';
import { SpaceInvadersModal } from '../components/SpaceInvadersModal';
import { event } from '../analytics';
import { ANALYSIS_LABELS, ANALYSIS_TYPES } from '../constants/analysisLabels';

interface StepExecution {
  id: string;
  harmAnalysisPerformed: boolean;
  harmCategoryPasses: number;
  harmCategoryFailures: number;
  failedCategories: string[];
  passedCategories: string[];
  failedCategoryNames: string[];
  passedCategoryNames: string[];
  // Granite Guardian 3.0 step fields
  graniteAnalysisPerformed: boolean;
  graniteCategoriesChecked: number;
  graniteCategoriesPassed: number;
  graniteCategoriesFailed: number;
  graniteOverallScore: number;
  graniteRiskDetected: boolean;
  // Individual Granite Guardian categories (Risk Detection) - boolean: true = risk detected
  graniteHarm?: boolean;
  graniteSocialBias?: boolean;
  graniteJailbreak?: boolean;
  graniteViolence?: boolean;
  graniteProfanity?: boolean;
  graniteSexualContent?: boolean;
  graniteUnethicalBehavior?: boolean;
  // Granite Guardian RAG Quality categories - boolean: true = issue detected (NOT good)
  graniteRelevance?: boolean;
  graniteGroundedness?: boolean;
  graniteAnswerRelevance?: boolean;
  graniteRagIssuesDetected?: boolean;
  // Semantic Analysis step fields
  embeddingAnalysisPerformed: boolean;
  embeddingGroundednessPassed: boolean;
  embeddingHallucinationPassed: boolean;
  embeddingTonePassed: boolean;
  embeddingBiasPassed: boolean;
  embeddingAllPassed: boolean;
  llmSemanticAnalysisPerformed: boolean;
  llmGroundednessPassed: boolean;
  llmHallucinationPassed: boolean;
  llmTonePassed: boolean;
  llmSemanticAllPassed: boolean;
}

interface SequenceExecution {
  id: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  totalSteps: number;
  completedSteps: number;
  failedSteps: number;
  overallSafetyScore?: number;
  overallIsSafe: boolean;
  totalHarmCategoryPasses: number;
  totalHarmCategoryFailures: number;
  // Granite Guardian 3.0 aggregated fields
  graniteAnalysisPerformed: boolean;
  graniteTotalChecks: number;
  graniteTotalPassed: number;
  graniteTotalFailed: number;
  graniteOverallScore?: number;
  graniteRiskDetected: boolean;
  graniteRagIssuesDetected: boolean;
  // Semantic Analysis aggregated fields
  embeddingAnalysisPerformed: boolean;
  embeddingAllPassed: boolean;
  llmSemanticAnalysisPerformed: boolean;
  llmSemanticAllPassed: boolean;
  sequence: {
    id: string;
    name: string;
  };
  stepExecutions: StepExecution[];
}

interface Sequence {
  id: string;
  name: string;
  status: string;
  scheduledEnabled: boolean;
  lastExecuted?: string;
  firstSuccessfulRun?: string;
  passRateAverage?: number;
  executionCount: number;
  scheduledDays: number[];
  scheduledFrequencyMinutes: number;
}

// Report configuration options - alphabetically sorted (except All Frameworks first)
const FRAMEWORK_OPTIONS = [
  { value: 'eu_ai_act', label: 'EU AI Act' },
  { value: 'finra_mrm', label: 'FINRA / SR 11-7 / Model Risk Management' },
  { value: 'hipaa_ai', label: 'HIPAA (AI in Healthcare)' },
  { value: 'iso_23894', label: 'ISO/IEC 23894 (AI Risk Management)' },
  { value: 'iso_27001', label: 'ISO/IEC 27001 (Information Security)' },
  { value: 'iso_42001', label: 'ISO/IEC 42001:2023 (AI Management)' },
  { value: 'nist_ai_rmf', label: 'NIST AI RMF 1.0' },
  { value: 'oecd_ai', label: 'OECD AI Principles' },
  { value: 'singapore_ai', label: 'Singapore Model AI Governance' },
  { value: 'soc2', label: 'SOC 2 Type II' },
  { value: 'uk_ai', label: 'UK AI Safety & Governance (DSIT/ICO)' },
];

// All framework values for default selection
const ALL_FRAMEWORK_VALUES = FRAMEWORK_OPTIONS.map(f => f.value);

const REPORT_TIME_PERIOD_OPTIONS = [
  { value: '1_week', label: 'Last 7 Days' },
  { value: '1_month', label: 'Last 30 Days' },
  { value: '6_months', label: 'Last 6 Months' },
  { value: '1_year', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom Date Range' },
];

// Helper to calculate date range from period
const getDateRangeFromPeriod = (period: string, customStart?: Date, customEnd?: Date): { startDate: Date; endDate: Date } => {
  const endDate = customEnd || new Date();
  let startDate = customStart || new Date();
  
  if (period !== 'custom') {
    switch (period) {
      case '1_week':
        startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1_month':
        startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '6_months':
        startDate = new Date(endDate.getTime() - 180 * 24 * 60 * 60 * 1000);
        break;
      case '1_year':
        startDate = new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
  }
  
  return { startDate, endDate };
};

// Helper to format date as readable string
const formatDateRange = (startDate: Date, endDate: Date): string => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return `${startDate.toLocaleDateString('en-US', options)} — ${endDate.toLocaleDateString('en-US', options)}`;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [complianceReportOpen, setComplianceReportOpen] = useState(false);
  const [reportConfigOpen, setReportConfigOpen] = useState(false);
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>(ALL_FRAMEWORK_VALUES);
  const [frameworkSelectOpen, setFrameworkSelectOpen] = useState(false);
  const [reportTimePeriod, setReportTimePeriod] = useState('1_month');
  const [customStartDate, setCustomStartDate] = useState<Date>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [customEndDate, setCustomEndDate] = useState<Date>(new Date());
  const [exportingPdf, setExportingPdf] = useState(false);
  const [currentReport, setCurrentReport] = useState<ComplianceReport | null>(null);
  const [creatingReport, setCreatingReport] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  
  // Dashboard filters
  const [sourceFilter, setSourceFilter] = useState<'all' | 'sequences' | 'api'>('all');
  const [selectedSequenceIds, setSelectedSequenceIds] = useState<string[]>([]);
  const [selectedApiKeys, setSelectedApiKeys] = useState<string[]>([]);

  const eggClickRef = useRef({ safety: 0, overview: 0 });
  const [spaceInvadersOpen, setSpaceInvadersOpen] = useState(false);

  const handleEggSafetyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const egg = eggClickRef.current;
    if (egg.overview > 0) {
      egg.safety = 1;
      egg.overview = 0;
      return;
    }
    egg.safety = Math.min(egg.safety + 1, 3);
  };

  const handleEggOverviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const egg = eggClickRef.current;
    if (egg.safety < 3) {
      egg.safety = 0;
      egg.overview = 0;
      return;
    }
    egg.overview += 1;
    if (egg.overview >= 3) {
      egg.safety = 0;
      egg.overview = 0;
      event('dashboard_easter_egg', { event_category: 'easter_egg', event_label: 'space_invaders' });
      setSpaceInvadersOpen(true);
    }
  };

  // Calculate report date range
  const reportDateRange = getDateRangeFromPeriod(
    reportTimePeriod, 
    reportTimePeriod === 'custom' ? customStartDate : undefined,
    reportTimePeriod === 'custom' ? customEndDate : undefined
  );
  
  // GraphQL mutations for compliance reports
  const [createComplianceReport] = useMutation(CREATE_COMPLIANCE_REPORT);
  const [markReportExported] = useMutation(MARK_REPORT_EXPORTED);
  
  // Initialize time range from sessionStorage or default to '30_days'
  const [timeRange, setTimeRange] = useState<string>(() => {
    const savedTimeRange = sessionStorage.getItem('dashboardTimeRange');
    return savedTimeRange || '30_days';
  });
  
  // Save time range to sessionStorage whenever it changes
  React.useEffect(() => {
    sessionStorage.setItem('dashboardTimeRange', timeRange);
  }, [timeRange]);

  // Reset current report when time range changes
  useEffect(() => {
    setCurrentReport(null);
  }, [timeRange]);

  // Export report as PDF
  const handleExportPdf = async () => {
    if (!reportRef.current) return;
    event('report_export_pdf', { event_category: 'report', event_action: 'export_pdf' });
    setExportingPdf(true);
    try {
      const element = reportRef.current;
      
      // Store original styles to restore later
      const originalStyle = element.style.cssText;
      const dialogPaper = element.closest('.MuiDialog-paper') as HTMLElement;
      const originalDialogStyle = dialogPaper?.style.cssText || '';
      
      // Temporarily remove height constraints to capture full content
      element.style.overflow = 'visible';
      element.style.maxHeight = 'none';
      element.style.height = 'auto';
      if (dialogPaper) {
        dialogPaper.style.maxHeight = 'none';
        dialogPaper.style.overflow = 'visible';
      }
      
      // Wait for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowHeight: element.scrollHeight,
        height: element.scrollHeight,
      } as any);
      
      // Restore original styles
      element.style.cssText = originalStyle;
      if (dialogPaper) {
        dialogPaper.style.cssText = originalDialogStyle;
      }
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // Add margins to avoid content at edges
      const usableHeight = pdfHeight - (margin * 2);
      const usableWidth = pdfWidth - (margin * 2);
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaling to fit width with margins
      const scale = usableWidth / (imgWidth / 2); // Divide by 2 because we used scale: 2
      const scaledHeight = (imgHeight / 2) * scale;
      
      // Calculate how many pixels in the source image correspond to each page
      const sourcePixelsPerPage = (usableHeight / scale) * 2;
      
      // Add pages as needed - slice the source image properly
      let sourceY = 0;
      let pageNum = 0;
      
      while (sourceY < imgHeight) {
        if (pageNum > 0) {
          pdf.addPage();
        }
        
        // Create a temporary canvas for this page slice
        const pageCanvas = document.createElement('canvas');
        const ctx = pageCanvas.getContext('2d');
        if (!ctx) break;
        
        // Calculate height for this slice (may be less on last page)
        const sliceHeight = Math.min(sourcePixelsPerPage, imgHeight - sourceY);
        pageCanvas.width = imgWidth;
        pageCanvas.height = sliceHeight;
        
        // Draw the slice from the source canvas
        ctx.drawImage(
          canvas,
          0, sourceY, imgWidth, sliceHeight,  // Source rectangle
          0, 0, imgWidth, sliceHeight          // Destination rectangle
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        const scaledSliceHeight = (sliceHeight / 2) * scale;
        
        pdf.addImage(
          pageImgData, 
          'PNG', 
          margin,
          margin,
          usableWidth,
          scaledSliceHeight
        );
        
        sourceY += sourcePixelsPerPage;
        pageNum++;
      }
      
      // Mark report as exported if we have a current report
      if (currentReport?.reportId) {
        try {
          await markReportExported({ variables: { reportId: currentReport.reportId } });
        } catch (err) {
          console.error('Failed to mark report as exported:', err);
        }
      }
      
      const timestamp = new Date().toISOString().split('T')[0];
      const reportIdSuffix = currentReport?.reportId ? `-${currentReport.reportId}` : '';
      pdf.save(`AI-Safety-Compliance-Report-${timestamp}${reportIdSuffix}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setExportingPdf(false);
    }
  };
  
  const { data, loading, error, refetch } = useQuery(DASHBOARD_STATS_QUERY, {
    variables: { timeRange },
    pollInterval: 30000, // Refresh every 30 seconds
    notifyOnNetworkStatusChange: false, // Avoid loading toggles during refetch that disrupt typing
  });

  const { data: apiEvalData } = useQuery(API_EVALUATION_SUMMARY_QUERY, {
    variables: { timeRange },
    pollInterval: 60000,
    notifyOnNetworkStatusChange: false,
  });
  const apiEvalSummary = useMemo(() => {
    try { return apiEvalData?.apiEvaluationSummary ? JSON.parse(apiEvalData.apiEvaluationSummary) : null; }
    catch { return null; }
  }, [apiEvalData]);

  // Process execution data for charts
  const chartData = useMemo(() => {
    if (!data?.sequenceExecutions && !apiEvalSummary?.evaluations) return { timeline: [], safetyScores: [], categoryScores: [], categoryCounts: [] };

    // Apply source filter + per-source filters
    let executions: SequenceExecution[] = data?.sequenceExecutions || [];
    let apiEvals: any[] = apiEvalSummary?.evaluations || [];

    if (sourceFilter === 'api') {
      executions = [];
    } else if (sourceFilter === 'sequences') {
      apiEvals = [];
    }
    if (selectedSequenceIds.length > 0) {
      executions = executions.filter(e => selectedSequenceIds.includes(e.sequence.id));
    }
    if (selectedApiKeys.length > 0) {
      apiEvals = apiEvals.filter((ev: any) => selectedApiKeys.includes(ev.evaluation_key));
    }

    // Timeline data - show all executions with their completion status
    const seqTimeline = executions.slice().reverse().map((exec) => ({
      name: exec.sequence.name.length > 12 
        ? exec.sequence.name.substring(0, 12) + '...' 
        : exec.sequence.name,
      fullName: exec.sequence.name,
      execId: exec.id,
      shortId: exec.id.substring(0, 8),
      safetyScore: exec.overallSafetyScore || 0,
      steps: exec.totalSteps,
      completed: exec.status === 'completed' ? 1 : 0,
      failed: exec.status === 'failed' ? 1 : 0,
      pending: exec.status === 'pending' || exec.status === 'running' || exec.status === 'queued' ? 1 : 0,
      status: exec.status,
      time: new Date(exec.startedAt).toLocaleTimeString(),
      source: 'sequence' as const,
    }));

    const apiTimeline = apiEvals.map((ev: any) => ({
      name: (ev.evaluation_name || ev.evaluation_key || 'API Eval').length > 12
        ? (ev.evaluation_name || ev.evaluation_key || 'API Eval').substring(0, 12) + '...'
        : (ev.evaluation_name || ev.evaluation_key || 'API Eval'),
      fullName: ev.evaluation_name || ev.evaluation_key || 'API Eval',
      execId: ev.id,
      shortId: ev.id?.substring(0, 8) || '',
      safetyScore: ev.overall_harm_score || 0,
      steps: 1,
      completed: 1,
      failed: ev.is_safe === false ? 1 : 0,
      pending: 0,
      status: ev.is_safe === false ? 'unsafe' : 'completed',
      time: ev.created_at ? new Date(ev.created_at).toLocaleTimeString() : '',
      source: 'api' as const,
    }));

    // Combine and sort by time (most recent last for chart display)
    const timeline = [...seqTimeline, ...apiTimeline];

    // Safety scores over time - combine both sources
    const seqSafetyScores = executions
      .filter(exec => exec.overallSafetyScore !== null && exec.overallSafetyScore !== undefined)
      .slice().reverse()
      .map((exec) => ({
        name: exec.sequence.name.length > 15 
          ? exec.sequence.name.substring(0, 15) + '...' 
          : exec.sequence.name,
        fullName: exec.sequence.name,
        execId: exec.id,
        shortId: exec.id.substring(0, 8),
        score: exec.overallSafetyScore,
        safe: exec.overallIsSafe ? 100 : 0,
        source: 'sequence' as const,
      }));

    const apiSafetyScores = apiEvals
      .filter((ev: any) => ev.overall_harm_score !== null && ev.overall_harm_score !== undefined)
      .map((ev: any) => ({
        name: (ev.evaluation_name || ev.evaluation_key || 'API').length > 15
          ? (ev.evaluation_name || ev.evaluation_key || 'API').substring(0, 15) + '...'
          : (ev.evaluation_name || ev.evaluation_key || 'API'),
        fullName: ev.evaluation_name || ev.evaluation_key || 'API Eval',
        execId: ev.id,
        shortId: ev.id?.substring(0, 8) || '',
        score: ev.overall_harm_score,
        safe: ev.is_safe ? 100 : 0,
        source: 'api' as const,
      }));

    const safetyScores = [...seqSafetyScores, ...apiSafetyScores];

    // Category scores over time
    const previousScores: { [key: string]: number } = {};
    let previousOverallScore: number | null = null;
    const categoryScores = executions.slice().reverse().map((exec) => {
      // Handle overall score - maintain previous value on failure
      const actualScore = exec.overallSafetyScore ?? 0;
      let overallScore: number | null = actualScore;
      let overallFailed = false;
      
      // Check if this is a failure (score is 0 or very low when marked unsafe)
      if (actualScore === 0 || (actualScore < 50 && !exec.overallIsSafe)) {
        overallFailed = true;
        // Use previous score to maintain line continuity
        if (previousOverallScore !== null && previousOverallScore > 0) {
          overallScore = previousOverallScore;
        }
      }
      
      // Update previous score for successful runs
      if (actualScore > 0 && !overallFailed) {
        previousOverallScore = actualScore;
      }
      
      const rawOverall = overallScore ?? exec.overallSafetyScore ?? 0;
      const overallNum = typeof rawOverall === 'number' && Number.isFinite(rawOverall) ? rawOverall : undefined;
      const categoryData: any = {
        name: exec.sequence.name.length > 12 
          ? exec.sequence.name.substring(0, 12) + '...' 
          : exec.sequence.name,
        fullName: exec.sequence.name,
        execId: exec.id,
        shortId: exec.id.substring(0, 8),
        time: new Date(exec.startedAt).toLocaleTimeString(),
        overall: overallNum,
        _failures: { overall: overallFailed } as any, // Track which categories failed
        source: 'sequence',
      };

      // Calculate per-category scores from step executions
      const categoryCounts: { [key: string]: { passes: number; failures: number } } = {};
      
      exec.stepExecutions.forEach(step => {
        // Llama Guard 3 harm categories
        if (step.harmAnalysisPerformed) {
          // Count passes and failures for each category using category names
          step.passedCategoryNames.forEach((catName) => {
            if (!categoryCounts[catName]) {
              categoryCounts[catName] = { passes: 0, failures: 0 };
            }
            categoryCounts[catName].passes += 1;
          });
          
          step.failedCategoryNames.forEach((catName) => {
            if (!categoryCounts[catName]) {
              categoryCounts[catName] = { passes: 0, failures: 0 };
            }
            categoryCounts[catName].failures += 1;
          });
        }
        
        // Regulatory Compliance Analysis (Standards & Regulatory Compliance)
        if (step.graniteAnalysisPerformed) {
          // Risk Detection: false = no risk (pass), true = risk detected (fail)
          const complianceRiskCategories = [
            { key: 'Compliance: Harm', value: step.graniteHarm },
            { key: 'Compliance: Social Bias', value: step.graniteSocialBias },
            { key: 'Compliance: Jailbreak', value: step.graniteJailbreak },
            { key: 'Compliance: Violence', value: step.graniteViolence },
            { key: 'Compliance: Profanity', value: step.graniteProfanity },
            { key: 'Compliance: Sexual Content', value: step.graniteSexualContent },
            { key: 'Compliance: Unethical', value: step.graniteUnethicalBehavior },
          ];
          
          complianceRiskCategories.forEach(({ key, value }) => {
            if (value !== undefined && value !== null) {
              if (!categoryCounts[key]) {
                categoryCounts[key] = { passes: 0, failures: 0 };
              }
              // false = no risk detected = pass, true = risk detected = fail
              if (value === false) {
                categoryCounts[key].passes++;
              } else {
                categoryCounts[key].failures++;
              }
            }
          });
          
          // RAG Quality Categories: false = good quality (pass), true = issue detected (fail)
          const complianceRagCategories = [
            { key: 'Compliance: RAG Relevance', value: step.graniteRelevance },
            { key: 'Compliance: RAG Grounded', value: step.graniteGroundedness },
            { key: 'Compliance: RAG Answer', value: step.graniteAnswerRelevance },
          ];
          
          complianceRagCategories.forEach(({ key, value }) => {
            if (value !== undefined && value !== null) {
              if (!categoryCounts[key]) {
                categoryCounts[key] = { passes: 0, failures: 0 };
              }
              // false = no issue = pass, true = issue detected = fail
              if (value === false) {
                categoryCounts[key].passes++;
              } else {
                categoryCounts[key].failures++;
              }
            }
          });
        }
        
        // User Perception & Experience Analysis (Combined LLM + Embedding)
        // LLM is authoritative: if LLM passes, it passes. If LLM fails, fail.
        // Check actual data presence, not just the "performed" flags
        const hasLlmData = step.llmGroundednessPassed !== undefined && step.llmGroundednessPassed !== null ||
                          step.llmHallucinationPassed !== undefined && step.llmHallucinationPassed !== null ||
                          step.llmTonePassed !== undefined && step.llmTonePassed !== null;
        const hasEmbedData = step.embeddingGroundednessPassed !== undefined && step.embeddingGroundednessPassed !== null ||
                            step.embeddingHallucinationPassed !== undefined && step.embeddingHallucinationPassed !== null ||
                            step.embeddingTonePassed !== undefined && step.embeddingTonePassed !== null;
        
        if (hasLlmData || hasEmbedData) {
          // Helper: combine results - LLM is authoritative
          const combineResults = (embedPassed: boolean | null | undefined, llmPassed: boolean | null | undefined): boolean | null => {
            // If LLM has data, use it (LLM is authoritative)
            if (llmPassed === true) return true;
            if (llmPassed === false) return false;
            // LLM didn't run or null - use embedding result
            if (embedPassed === true) return true;
            if (embedPassed === false) return false;
            // No data from either
            return null;
          };
          
          const perceptionCategories = [
            { key: 'Perception: Grounded', passed: combineResults(step.embeddingGroundednessPassed, step.llmGroundednessPassed) },
            { key: 'Perception: No Hallucination', passed: combineResults(step.embeddingHallucinationPassed, step.llmHallucinationPassed) },
            { key: 'Perception: Appropriate Tone', passed: combineResults(step.embeddingTonePassed, step.llmTonePassed) },
          ];
          
          perceptionCategories.forEach(({ key, passed }) => {
            // Only count if we have a definite true/false value
            if (passed !== undefined && passed !== null) {
              if (!categoryCounts[key]) {
                categoryCounts[key] = { passes: 0, failures: 0 };
              }
              if (passed === true) {
                categoryCounts[key].passes++;
              } else {
                categoryCounts[key].failures++;
              }
            }
          });
        }
      });

      // Calculate score for each category (passes / (passes + failures) * 100)
      Object.entries(categoryCounts).forEach(([cat, counts]) => {
        const total = counts.passes + counts.failures;
        if (total > 0) {
          const score = (counts.passes / total) * 100;
          const safe = Number.isFinite(score) ? score : undefined;
          if (safe !== undefined) {
            categoryData[cat] = safe;
            categoryData._failures[cat] = false;
            previousScores[cat] = safe;
          }
        }
      });

      return categoryData;
    });

    // Add API evaluation entries to categoryScores for heatmap
    apiEvals.forEach((ev: any) => {
      const evName = ev.evaluation_name || ev.evaluation_key || 'API Eval';
      const rawOverall = ev.overall_harm_score ?? 0;
      const overallScore = Number.isFinite(Number(rawOverall)) ? Number(rawOverall) : undefined;
      const apiCategoryData: any = {
        name: evName.length > 12 ? evName.substring(0, 12) + '...' : evName,
        fullName: evName,
        execId: ev.id,
        shortId: ev.id?.substring(0, 8) || '',
        time: ev.created_at ? new Date(ev.created_at).toLocaleTimeString() : '',
        overall: overallScore,
        _failures: { overall: ev.is_safe === false } as any,
        source: 'api',
      };

      // Convert per-category data from backend
      const catScores = ev.category_scores || {};
      Object.entries(catScores).forEach(([cat, counts]: [string, any]) => {
        const total = (counts.passes || 0) + (counts.failures || 0);
        if (total > 0) {
          const pct = ((counts.passes || 0) / total) * 100;
          if (Number.isFinite(pct)) {
            apiCategoryData[cat] = pct;
            apiCategoryData._failures[cat] = false;
          }
        }
      });

      categoryScores.push(apiCategoryData);
    });

    // Category counts for bar chart - aggregate across all executions
    const categoryCounts: { [key: string]: { passes: number; failures: number; total: number } } = {};
    
    executions.forEach(exec => {
      exec.stepExecutions.forEach(step => {
        // Llama Guard 3 harm categories
        if (step.harmAnalysisPerformed) {
          // Count passes for each category
          step.passedCategoryNames.forEach((catName) => {
            if (!categoryCounts[catName]) {
              categoryCounts[catName] = { passes: 0, failures: 0, total: 0 };
            }
            categoryCounts[catName].passes++;
            categoryCounts[catName].total++;
          });
          
          // Count failures for each category
          step.failedCategoryNames.forEach((catName) => {
            if (!categoryCounts[catName]) {
              categoryCounts[catName] = { passes: 0, failures: 0, total: 0 };
            }
            categoryCounts[catName].failures++;
            categoryCounts[catName].total++;
          });
        }
        
        // Regulatory Compliance Analysis (Standards & Regulatory Compliance)
        if (step.graniteAnalysisPerformed) {
          // Risk Detection: false = no risk (pass), true = risk detected (fail)
          const complianceRiskCategories = [
            { key: 'Compliance: Harm', value: step.graniteHarm },
            { key: 'Compliance: Social Bias', value: step.graniteSocialBias },
            { key: 'Compliance: Jailbreak', value: step.graniteJailbreak },
            { key: 'Compliance: Violence', value: step.graniteViolence },
            { key: 'Compliance: Profanity', value: step.graniteProfanity },
            { key: 'Compliance: Sexual Content', value: step.graniteSexualContent },
            { key: 'Compliance: Unethical', value: step.graniteUnethicalBehavior },
          ];
          
          complianceRiskCategories.forEach(({ key, value }) => {
            if (value !== undefined && value !== null) {
              if (!categoryCounts[key]) {
                categoryCounts[key] = { passes: 0, failures: 0, total: 0 };
              }
              // false = no risk = pass, true = risk detected = fail
              if (value === false) {
                categoryCounts[key].passes++;
              } else {
                categoryCounts[key].failures++;
              }
              categoryCounts[key].total++;
            }
          });
          
          // RAG Quality: false = good quality (pass), true = issue detected (fail)
          const complianceRagCategories = [
            { key: 'Compliance: RAG Relevance', value: step.graniteRelevance },
            { key: 'Compliance: RAG Grounded', value: step.graniteGroundedness },
            { key: 'Compliance: RAG Answer', value: step.graniteAnswerRelevance },
          ];
          
          complianceRagCategories.forEach(({ key, value }) => {
            if (value !== undefined && value !== null) {
              if (!categoryCounts[key]) {
                categoryCounts[key] = { passes: 0, failures: 0, total: 0 };
              }
              // false = no issue = pass, true = issue detected = fail
              if (value === false) {
                categoryCounts[key].passes++;
              } else {
                categoryCounts[key].failures++;
              }
              categoryCounts[key].total++;
            }
          });
        }
        
        // User Perception & Experience Analysis (Combined LLM + Embedding)
        // LLM is authoritative: if LLM passes, it passes. If LLM fails, fail.
        // Check actual data presence, not just the "performed" flags
        const hasLlmResults = step.llmGroundednessPassed !== undefined && step.llmGroundednessPassed !== null ||
                             step.llmHallucinationPassed !== undefined && step.llmHallucinationPassed !== null ||
                             step.llmTonePassed !== undefined && step.llmTonePassed !== null;
        const hasEmbedResults = step.embeddingGroundednessPassed !== undefined && step.embeddingGroundednessPassed !== null ||
                               step.embeddingHallucinationPassed !== undefined && step.embeddingHallucinationPassed !== null ||
                               step.embeddingTonePassed !== undefined && step.embeddingTonePassed !== null;
        
        if (hasLlmResults || hasEmbedResults) {
          // Helper: combine results - LLM is authoritative
          const combinePerceptionResults = (embedPassed: boolean | null | undefined, llmPassed: boolean | null | undefined): boolean | null => {
            // If LLM has data, use it (LLM is authoritative)
            if (llmPassed === true) return true;
            if (llmPassed === false) return false;
            // LLM didn't run or null - use embedding result
            if (embedPassed === true) return true;
            if (embedPassed === false) return false;
            // No data from either
            return null;
          };
          
          const perceptionCats = [
            { key: 'Perception: Grounded', passed: combinePerceptionResults(step.embeddingGroundednessPassed, step.llmGroundednessPassed) },
            { key: 'Perception: No Hallucination', passed: combinePerceptionResults(step.embeddingHallucinationPassed, step.llmHallucinationPassed) },
            { key: 'Perception: Appropriate Tone', passed: combinePerceptionResults(step.embeddingTonePassed, step.llmTonePassed) },
          ];
          
          perceptionCats.forEach(({ key, passed }) => {
            // Only count if we have a definite true/false value, not null/undefined
            if (passed !== undefined && passed !== null) {
              if (!categoryCounts[key]) {
                categoryCounts[key] = { passes: 0, failures: 0, total: 0 };
              }
              if (passed === true) {
                categoryCounts[key].passes++;
              } else {
                categoryCounts[key].failures++;
              }
              categoryCounts[key].total++;
            }
          });
        }
      });
    });
    
    // Merge API evaluation category counts
    if (apiEvalSummary?.category_counts) {
      Object.entries(apiEvalSummary.category_counts as Record<string, { passes: number; failures: number }>).forEach(([name, counts]) => {
        if (!categoryCounts[name]) {
          categoryCounts[name] = { passes: 0, failures: 0, total: 0 };
        }
        categoryCounts[name].passes += counts.passes || 0;
        categoryCounts[name].failures += counts.failures || 0;
        categoryCounts[name].total += (counts.passes || 0) + (counts.failures || 0);
      });
    }

    // Convert to array format for bar chart
    // Custom sort: group by analysis type, then alphabetically within each group
    const getCategoryGroup = (cat: string): number => {
      if (cat.startsWith('Compliance:')) return 2;  // Regulatory Compliance
      if (cat.startsWith('Perception:')) return 3;  // User Perception
      return 1; // General Safety (harm categories like "Violent Crimes", etc.)
    };
    
    const categoryCountsArray = Object.entries(categoryCounts)
      .map(([name, counts]) => ({
        name: name,
        passes: counts.passes,
        failures: counts.failures,
        total: counts.total,
        passRate: counts.total > 0 ? (counts.passes / counts.total) * 100 : 0,
      }))
      .sort((a, b) => {
        const groupA = getCategoryGroup(a.name);
        const groupB = getCategoryGroup(b.name);
        if (groupA !== groupB) return groupA - groupB;
        return a.name.localeCompare(b.name); // Alphabetical within group
      });

    return { timeline, safetyScores, categoryScores, categoryCounts: categoryCountsArray };
  }, [data, apiEvalSummary, sourceFilter, selectedSequenceIds, selectedApiKeys]);

  // Only show full-page loading on initial load (when we have no cached data)
  // During refetches/polling, we keep showing the existing data to avoid flicker
  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error">Error loading dashboard: {error.message}</Typography>
        <Button data-ga-label="Dashboard Retry" onClick={() => refetch()} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  const stats = data?.dashboardStats || {
    currentSafetyScore: 0,
    totalHarmCategoryPasses: 0,
    totalHarmCategoryFailures: 0,
    overallIsSafe: true,
    totalExecutions30Days: 0,
    completedExecutions30Days: 0,
    activeAlertsCount: 0,
  };

  const sequences: Sequence[] = data?.sequences || [];
  const allExecutions: SequenceExecution[] = data?.sequenceExecutions || [];
  
  // Apply source filter + per-source filters
  let executions: SequenceExecution[] = allExecutions;
  if (sourceFilter === 'api') {
    executions = [];
  } else if (selectedSequenceIds.length > 0) {
    executions = allExecutions.filter(e => selectedSequenceIds.includes(e.sequence.id));
  }

  let filteredApiEvals: any[] = apiEvalSummary?.evaluations || [];
  if (sourceFilter === 'sequences') {
    filteredApiEvals = [];
  } else if (selectedApiKeys.length > 0) {
    filteredApiEvals = filteredApiEvals.filter((ev: any) => selectedApiKeys.includes(ev.evaluation_key));
  }

  const activeSequences = sequences.filter(s => s.status === 'active').length;
  const scheduledSequences = sequences.filter(s => s.scheduledEnabled).length;
  const totalExecutions = sourceFilter === 'api' ? 0 : (selectedSequenceIds.length > 0 ? executions.length : (stats.totalExecutions30Days || 0));
  const completedExecutions = sourceFilter === 'api' ? 0 : (selectedSequenceIds.length > 0 ? executions.filter(e => e.status === 'completed').length : (stats.completedExecutions30Days || 0));
  const successRate = totalExecutions > 0 
    ? ((completedExecutions / totalExecutions) * 100).toFixed(1)
    : 0;

  const filteredApiCompleted = sourceFilter === 'sequences' ? 0 : filteredApiEvals.length;
  const filteredApiSafe = sourceFilter === 'sequences' ? 0 : filteredApiEvals.filter((ev: any) => ev.is_safe !== false).length;
  const filteredApiUnsafe = sourceFilter === 'sequences' ? 0 : filteredApiEvals.filter((ev: any) => ev.is_safe === false).length;
  const filteredApiGraniteRisk = sourceFilter === 'sequences' ? 0 : filteredApiEvals.filter((ev: any) => {
    const cats = ev.category_scores || {};
    return Object.entries(cats).some(([k, v]: [string, any]) => k.startsWith('Compliance:') && (v.failures || 0) > 0);
  }).length;

  // Calculate estimated daily scheduled executions
  const estimatedDailyExecutions = sequences
    .filter(s => s.scheduledEnabled)
    .reduce((total, seq) => {
      const runsPerDay = seq.scheduledFrequencyMinutes > 0 
        ? Math.floor(1440 / seq.scheduledFrequencyMinutes) 
        : 1;
      const daysPerWeek = seq.scheduledDays?.length || 7;
      const avgDailyRuns = (runsPerDay * daysPerWeek) / 7;
      return total + avgDailyRuns;
    }, 0);

  // Handler to open the configuration modal
  const handleOpenReportConfig = () => {
    startTransition(() => {
      setSelectedFrameworks([]); // Clear selections when opening modal
    });
    setReportConfigOpen(true);
  };

  // Handler to generate the compliance report after configuration
  const handleGenerateReport = async () => {
    event('report_generate', { event_category: 'report', event_action: 'generate' });
    setReportConfigOpen(false);
    setComplianceReportOpen(true);
    setCurrentReport(null); // Reset to generate new report with new config
    
    setCreatingReport(true);
    try {
      const result = await createComplianceReport({
        variables: {
          input: {
            timeRange: reportTimePeriod,
            reportType: 'safety',
            safetyScore: stats.currentSafetyScore || 0,
            activeSequences,
            totalExecutions,
            activeAlerts: stats.activeAlertsCount || 0,
            categoriesMonitored: chartData.categoryCounts.length,
            scheduledSequences,
            sequenceIds: sequences.map(s => s.id),
            categoryResults: JSON.stringify(chartData.categoryCounts),
          }
        }
      });
      
      if (result.data?.createComplianceReport) {
        setCurrentReport(result.data.createComplianceReport);
      }
    } catch (err) {
      console.error('Failed to create compliance report:', err);
    } finally {
      setCreatingReport(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f0fdfa', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h2" sx={{ 
            fontWeight: 'bold', 
            background: 'linear-gradient(135deg, #0f172a 0%, #134e4a 50%, #1e3a5f 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Dashboard
          </Typography>
          <Typography variant="body1" component="p" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {user?.companyName ? `${user.companyName}'s` : 'Your'} AI{' '}
            <Box
              component="span"
              onClick={handleEggSafetyClick}
              sx={{
                cursor: 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              Safety
            </Box>{' '}
            <Box
              component="span"
              onClick={handleEggOverviewClick}
              sx={{
                cursor: 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              Overview
            </Box>
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            data-ga-label="Generate Compliance Report"
            variant="contained"
            startIcon={<Assessment />}
            onClick={handleOpenReportConfig}
            sx={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #152a4a 100%)',
              '&:hover': { 
                background: 'linear-gradient(135deg, #2d4a6f 0%, #1e3a5f 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 16px rgba(13, 148, 136, 0.35)',
              },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Generate Compliance Report
          </Button>
          
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
              sx={{ bgcolor: 'white' }}
            >
              <MenuItem value="30_min">Last 30 Minutes</MenuItem>
              <MenuItem value="1_hour">Last Hour</MenuItem>
              <MenuItem value="24_hours">Last 24 Hours</MenuItem>
              <MenuItem value="1_week">Last Week</MenuItem>
              <MenuItem value="30_days">Last 30 Days</MenuItem>
              <MenuItem value="all_time">All Time</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Filter Bar */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 3, 
        p: 2, 
        bgcolor: 'white', 
        borderRadius: 2, 
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}>
        <FilterList sx={{ color: '#64748b', fontSize: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mr: 1 }}>
          Filters:
        </Typography>

        {/* Source Filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Source</InputLabel>
          <Select
            value={sourceFilter}
            label="Source"
            onChange={(e) => {
              setSourceFilter(e.target.value as 'all' | 'sequences' | 'api');
              if (e.target.value === 'api') setSelectedSequenceIds([]);
              if (e.target.value === 'sequences') setSelectedApiKeys([]);
            }}
            sx={{ bgcolor: 'white' }}
          >
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="sequences">Sequences Only</MenuItem>
            <MenuItem value="api">API Evaluations Only</MenuItem>
          </Select>
        </FormControl>

        {/* Sequence Filter (hidden when source = api) */}
        {sourceFilter !== 'api' && sequences.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 320 }}>
            <InputLabel>Sequences</InputLabel>
            <Select
              multiple
              value={selectedSequenceIds}
              label="Sequences"
              onChange={(e) => setSelectedSequenceIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              input={<OutlinedInput label="Sequences" />}
              renderValue={(selected) => {
                if (selected.length === 0) return 'All';
                const names = selected.map(id => sequences.find(s => s.id === id)?.name || id);
                return names.length <= 2 ? names.join(', ') : `${names.length} selected`;
              }}
              sx={{ bgcolor: 'white' }}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <ListItem sx={{ borderBottom: 1, borderColor: 'divider', py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                <Button data-ga-label="Sequences Select all" size="small" onClick={(e) => { e.stopPropagation(); setSelectedSequenceIds(sequences.map(s => s.id)); }} sx={{ textTransform: 'none', mr: 1 }}>Select all</Button>
                <Button data-ga-label="Sequences Remove all" size="small" onClick={(e) => { e.stopPropagation(); setSelectedSequenceIds([]); }} sx={{ textTransform: 'none' }}>Remove all</Button>
              </ListItem>
              {sequences.map(seq => (
                <MenuItem key={seq.id} value={seq.id}>
                  <Checkbox checked={selectedSequenceIds.includes(seq.id)} size="small" />
                  <ListItemText primary={seq.name} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* API Evaluations Filter (hidden when source = sequences) */}
        {sourceFilter !== 'sequences' && apiEvalSummary?.key_breakdown && apiEvalSummary.key_breakdown.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 320 }}>
            <InputLabel>API Evaluations</InputLabel>
            <Select
              multiple
              value={selectedApiKeys}
              label="API Evaluations"
              onChange={(e) => setSelectedApiKeys(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              input={<OutlinedInput label="API Evaluations" />}
              renderValue={(selected) => {
                if (selected.length === 0) return 'All';
                return selected.length <= 2 ? selected.join(', ') : `${selected.length} selected`;
              }}
              sx={{ bgcolor: 'white' }}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              <ListItem sx={{ borderBottom: 1, borderColor: 'divider', py: 0.5 }} onClick={(e) => e.stopPropagation()}>
                <Button data-ga-label="API Evaluations Select all" size="small" onClick={(e) => { e.stopPropagation(); setSelectedApiKeys(apiEvalSummary.key_breakdown.map((k: any) => k.key)); }} sx={{ textTransform: 'none', mr: 1 }}>Select all</Button>
                <Button data-ga-label="API Evaluations Remove all" size="small" onClick={(e) => { e.stopPropagation(); setSelectedApiKeys([]); }} sx={{ textTransform: 'none' }}>Remove all</Button>
              </ListItem>
              {apiEvalSummary.key_breakdown.map((k: any) => (
                <MenuItem key={k.key} value={k.key}>
                  <Checkbox checked={selectedApiKeys.includes(k.key)} size="small" />
                  <ListItemText 
                    primary={k.key} 
                    secondary={`${k.total} evals · ${k.pass_rate}% pass`}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {(sourceFilter !== 'all' || selectedSequenceIds.length > 0 || selectedApiKeys.length > 0) && (
          <Button 
            size="small" 
            data-ga-label="Clear Filters"
            onClick={() => {
              setSourceFilter('all');
              setSelectedSequenceIds([]);
              setSelectedApiKeys([]);
            }}
            sx={{ textTransform: 'none', color: '#64748b', ml: 'auto' }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Combined Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Safety Score */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: stats.currentSafetyScore >= 90
              ? 'linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)'
              : stats.currentSafetyScore >= 75
              ? 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)'
              : 'linear-gradient(135deg, #be123c 0%, #e11d48 100%)',
            color: 'white',
            boxShadow: '0 8px 24px rgba(13, 148, 136, 0.3)',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                    {stats.currentSafetyScore.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Safety Score
                  </Typography>
                  <Chip
                    label={stats.currentSafetyScore >= 90 ? 'Safe' : stats.currentSafetyScore >= 75 ? 'Caution' : 'At Risk'}
                    size="small"
                    sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }}
                  />
                </Box>
                <TrendingUp sx={{ fontSize: 48, color: 'white', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Assessments (combined) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                    {totalExecutions + filteredApiCompleted}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Total Assessments
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 0.5 }}>
                    {totalExecutions} sequences · {filteredApiCompleted} API evals
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 48, opacity: 0.85, color: 'white' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Active Sequences */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #152a4a 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-2px)' }
          }} onClick={() => navigate('/sequences')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                    {activeSequences}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Active Sequences
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 0.5 }}>
                    {scheduledSequences} scheduled
                  </Typography>
                </Box>
                <PlayArrow sx={{ fontSize: 48, opacity: 0.85, color: 'white' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            height: '100%',
            background: stats.activeAlertsCount === 0
              ? 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)'
              : 'linear-gradient(135deg, #be123c 0%, #e11d48 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-2px)' }
          }} onClick={() => navigate('/alerts')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                    {stats.activeAlertsCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    Active Alerts
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', mt: 0.5 }}>
                    {successRate}% execution success rate
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 48, color: 'white', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sequence Executions & API Evaluations side by side */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Sequence Executions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }} onClick={() => navigate('/results')}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Sequence Executions
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0891b2' }}>{totalExecutions}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Runs</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>{completedExecutions}</Typography>
                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: totalExecutions - completedExecutions > 0 ? '#e11d48' : '#1e3a5f' }}>{totalExecutions - completedExecutions}</Typography>
                    <Typography variant="caption" color="text.secondary">Failed</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* API Evaluations */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }} onClick={() => navigate('/api-results')}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                API Evaluations
              </Typography>
              {filteredApiCompleted > 0 ? (
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0891b2' }}>{filteredApiCompleted}</Typography>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>{filteredApiSafe}</Typography>
                    <Typography variant="caption" color="text.secondary">Safe</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: filteredApiUnsafe > 0 ? '#e11d48' : '#1e3a5f' }}>{filteredApiUnsafe}</Typography>
                    <Typography variant="caption" color="text.secondary">Unsafe</Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: filteredApiGraniteRisk > 0 ? '#e11d48' : '#1e3a5f' }}>{filteredApiGraniteRisk}</Typography>
                    <Typography variant="caption" color="text.secondary">Risks</Typography>
                  </Box>
                </Grid>
                {apiEvalSummary.key_breakdown && apiEvalSummary.key_breakdown.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  {apiEvalSummary.key_breakdown.slice(0, 5).map((k: any) => (
                    <Box key={k.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{k.key}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">{k.total} evals</Typography>
                        <Chip
                          label={`${k.pass_rate}%`}
                          size="small"
                          sx={{
                            bgcolor: k.pass_rate >= 90 ? '#e0f2fe' : k.pass_rate >= 70 ? '#fef3c7' : '#ffe4e6',
                            color: k.pass_rate >= 90 ? '#1e3a5f' : k.pass_rate >= 70 ? '#92400e' : '#e11d48',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Grid>
                )}
              </Grid>
              ) : (
              <Typography variant="body2" color="text.secondary">
                No API evaluations yet. Use the Evaluation API to submit LLM outputs for analysis.
              </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Execution Status Breakdown */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Assessment Status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Breakdown of all assessment outcomes (sequences + API evals)
              </Typography>
              {(() => {
                const apiSafe = filteredApiSafe;
                const apiUnsafe = filteredApiUnsafe;
                const failedExecutions = totalExecutions - completedExecutions;
                const totalAll = totalExecutions + apiSafe + apiUnsafe;
                
                const pieData = [
                  { name: 'Seq Completed', value: completedExecutions, color: '#1e3a5f' },
                  { name: 'Seq Failed', value: failedExecutions, color: '#f43f5e' },
                  { name: 'API Safe', value: apiSafe, color: '#0891b2' },
                  { name: 'API Unsafe', value: apiUnsafe, color: '#be123c' },
                ].filter(d => d.value > 0);
                
                if (totalAll === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No assessment data available</Typography>
                      <Button 
                        variant="contained" 
                        sx={{ mt: 2 }}
                        onClick={() => navigate('/sequences')}
                      >
                        Run a Sequence
                      </Button>
                    </Box>
                  );
                }
                
                return (
                  <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={95}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value: number, name: string) => [`${value} (${((value / totalAll) * 100).toFixed(1)}%)`, name]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center label */}
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '42%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e3a5f', lineHeight: 1 }}>
                        {totalAll}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total
                      </Typography>
                    </Box>
                    {/* Legend below chart */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                      {pieData.map((entry, index) => (
                        <Box key={`legend-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: entry.color }} />
                          <Typography variant="body2" color="text.secondary">
                            {entry.value} {entry.name} ({((entry.value / totalAll) * 100).toFixed(0)}%)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Failures */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', height: '100%' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                Recent Failures & Unsafe Results
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Failed executions and unsafe API evaluations
              </Typography>
              {(() => {
                const seqFailures = chartData.timeline
                  .filter(exec => exec.status === 'failed' && exec.source === 'sequence')
                  .slice(0, 5);
                const apiFailures = chartData.timeline
                  .filter(exec => (exec.status === 'unsafe' || exec.failed === 1) && exec.source === 'api')
                  .slice(0, 5);
                const failures = [...seqFailures, ...apiFailures].slice(0, 5);
                
                if (failures.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                      <CheckCircle sx={{ fontSize: 48, color: '#16a34a', mb: 1 }} />
                      <Typography color="text.secondary">No recent failures!</Typography>
                      <Typography variant="caption" color="text.secondary">All assessments passed successfully</Typography>
                    </Box>
                  );
                }
                
                return (
                  <List sx={{ p: 0 }}>
                    {failures.map((exec, idx) => (
                      <ListItem 
                        key={exec.execId}
                        sx={{ 
                          px: 2, 
                          py: 1.5,
                          bgcolor: idx % 2 === 0 ? '#fef2f2' : 'transparent',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#fee2e2' }
                        }}
                        onClick={() => exec.source === 'api' ? navigate('/api-results') : navigate(`/bias-results?executionId=${exec.execId}`)}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Error sx={{ color: '#f43f5e' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={exec.fullName}
                          secondary={`${exec.source === 'api' ? '[API] ' : ''}${exec.time} • ID: ${exec.shortId}`}
                          primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Category Counts */}
        {chartData.categoryCounts && chartData.categoryCounts.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Risk Category Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Understand your riskiest categories at a glance - see which categories have the most failures and may need attention.
                </Typography>
                
                {/* Analysis Type Legend with Tooltips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 2, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.GENERAL_SAFETY].tooltip}>
                    <Chip
                      label={ANALYSIS_LABELS[ANALYSIS_TYPES.GENERAL_SAFETY].shortName}
                      size="small"
                      sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, borderRadius: '6px' }}
                      tabIndex={0}
                    />
                  </AccessibleTooltip>
                  <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.REGULATORY_COMPLIANCE].tooltip}>
                    <Chip
                      label={ANALYSIS_LABELS[ANALYSIS_TYPES.REGULATORY_COMPLIANCE].shortName}
                      size="small"
                      sx={{ bgcolor: '#e0f2fe', color: '#0e7490', fontWeight: 600, borderRadius: '6px' }}
                      tabIndex={0}
                    />
                  </AccessibleTooltip>
                  <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.USER_PERCEPTION].tooltip}>
                    <Chip
                      label={ANALYSIS_LABELS[ANALYSIS_TYPES.USER_PERCEPTION].shortName}
                      size="small"
                      sx={{ bgcolor: '#e0f2fe', color: '#0e7490', fontWeight: 600, borderRadius: '6px' }}
                      tabIndex={0}
                    />
                  </AccessibleTooltip>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                  Total passes and failures for each risk category across all sequence executions and API evaluations in the selected time range
                </Typography>
                <ResponsiveContainer width="100%" height={450}>
                  <BarChart 
                    data={chartData.categoryCounts}
                    margin={{ top: 40, right: 30, left: 20, bottom: 120 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      interval={0}
                      style={{ fontSize: '11px' }}
                      tick={{ dy: 10 }}
                      label={{ value: 'Risk Category', position: 'insideBottom', offset: -25 }}
                    />
                    <YAxis 
                      label={{ value: 'Count', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Paper sx={{ p: 2, maxWidth: 280 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {data.name}
                              </Typography>
                                <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2" sx={{ 
                                    background: 'linear-gradient(90deg, #1e3a5f 0%, #0ea5e9 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                  }}>
                                    ✓ Passes:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {data.passes}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2" sx={{ color: '#f43f5e' }}>
                                    ✗ Failures:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {data.failures}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #e5e7eb' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    Total:
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {data.total}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    Pass Rate:
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 'bold',
                                      color: data.passRate >= 90 ? '#1e3a5f' : data.passRate >= 75 ? '#0891b2' : '#f43f5e'
                                    }}
                                  >
                                    {data.passRate.toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: '10px' }} />
                    <Bar dataKey="passes" stackId="a" fill="#1e3a5f" name="Passes" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="failures" stackId="a" fill="#f43f5e" name="Failures" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Risk Category Heatmap */}
        {chartData.categoryScores.length > 0 && (() => {
          const hasAnyValidScore = (data: any): boolean => {
            if (data.overall !== undefined && data.overall !== null && Number.isFinite(Number(data.overall))) return true;
            return Object.keys(data).some(key => {
              if (key === 'name' || key === 'fullName' || key === 'execId' || key === 'shortId' || key === 'time' || key === 'overall' || key === '_failures') return false;
              const v = data[key];
              return v !== undefined && v !== null && Number.isFinite(Number(v));
            });
          };
          // Only show columns that have at least one valid score (no empty columns)
          const scoresWithData = chartData.categoryScores.filter(hasAnyValidScore);

          // Extract all unique categories from the data
          const allCategories = new Set<string>();
          scoresWithData.forEach(data => {
            Object.keys(data).forEach(key => {
              if (key !== 'name' && key !== 'fullName' && key !== 'execId' && key !== 'shortId' && key !== 'time' && key !== 'overall' && key !== '_failures') {
                allCategories.add(key);
              }
            });
          });
          
          // Custom sort: group by analysis type, then alphabetically within each group
          const getCategoryGroup = (cat: string): number => {
            if (cat.startsWith('Compliance:')) return 2;  // Regulatory Compliance
            if (cat.startsWith('Perception:')) return 3;  // User Perception
            return 1; // General Safety (harm categories like "Violent Crimes", etc.)
          };
          
          const categoryArray = Array.from(allCategories).sort((a, b) => {
            const groupA = getCategoryGroup(a);
            const groupB = getCategoryGroup(b);
            if (groupA !== groupB) return groupA - groupB;
            return a.localeCompare(b); // Alphabetical within group
          });
          
          // Filter out invalid categories
          const validCategories = categoryArray.filter(cat => cat && typeof cat === 'string' && cat.length > 0);
          
          // Normalize score: finite number or undefined (avoids NaN/0 from bad data)
          const safeScore = (v: any): number | undefined => {
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : undefined;
          };
          const formatScoreCell = (v: any): string => {
            const n = safeScore(v);
            return n === undefined ? '—' : `${Math.round(n)}%`;
          };
          const formatScoreTooltip = (v: any): string => {
            const n = safeScore(v);
            return n === undefined ? 'No Data' : `${Number(n).toFixed(1)}%`;
          };

          // Prepare heatmap data: rows are categories, columns are executions/evals (only scoresWithData)
          const heatmapData = validCategories.map(category => {
            const row: any = { category };
            scoresWithData.forEach((execution, idx) => {
              const raw = execution[category];
              row[`exec_${idx}`] = {
                score: safeScore(raw),
                execId: execution.execId,
                execName: execution.name,
                fullName: execution.fullName,
                shortId: execution.shortId,
                isFailed: false,
                source: execution.source || 'sequence',
              };
            });
            return row;
          });

          // Add overall row
          const overallRow: any = { category: 'Overall Score' };
          scoresWithData.forEach((execution, idx) => {
            overallRow[`exec_${idx}`] = {
              score: safeScore(execution.overall),
              execId: execution.execId,
              execName: execution.name,
              fullName: execution.fullName,
              shortId: execution.shortId,
              isFailed: execution._failures?.overall === true,
              source: execution.source || 'sequence',
            };
          });
          heatmapData.unshift(overallRow);

          // Get cell color based on score - completely different palette
          const getCellColor = (score: number | undefined, isFailed: boolean) => {
            if (score === undefined || score === null || !Number.isFinite(score)) return '#cbd5e1'; // Slate-300 for no data
            if (score >= 90) return '#0284c7'; // Sky-600 (safe)
            if (score >= 75) return '#1e3a5f'; // Violet-600 (good)
            if (score >= 50) return '#0891b2'; // Fuchsia-600 (warning)
            return '#be123c'; // Rose-700 (critical)
          };

          // Get text color based on score
          const getTextColor = (score: number | undefined, isFailed: boolean) => {
            if (score === undefined || score === null || !Number.isFinite(score)) return '#6b7280';
            return '#ffffff';
          };

          const executionCount = scoresWithData.length;
          const cellWidth = Math.max(60, Math.min(100, 800 / executionCount));

          return validCategories.length > 0 && executionCount > 0 && (
            <Grid item xs={12}>
              <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Risk Category Performance Heatmap
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Visual snapshot of safety scores across all risk categories — sequences and API evaluations
                  </Typography>
                  
                  {/* Analysis Type Legend with Tooltips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 2, bgcolor: '#f0fdfa', borderRadius: 1 }}>
                    <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.GENERAL_SAFETY].tooltip}>
                      <Chip
                        label={ANALYSIS_LABELS[ANALYSIS_TYPES.GENERAL_SAFETY].shortName}
                        size="small"
                        sx={{ bgcolor: '#e0f2fe', color: '#152a4a', fontWeight: 500 }}
                        tabIndex={0}
                      />
                    </AccessibleTooltip>
                    <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.REGULATORY_COMPLIANCE].tooltip}>
                      <Chip
                        label={ANALYSIS_LABELS[ANALYSIS_TYPES.REGULATORY_COMPLIANCE].shortName}
                        size="small"
                        sx={{ bgcolor: '#e0f2fe', color: '#0e7490', fontWeight: 500 }}
                        tabIndex={0}
                      />
                    </AccessibleTooltip>
                    <AccessibleTooltip title={ANALYSIS_LABELS[ANALYSIS_TYPES.USER_PERCEPTION].tooltip}>
                      <Chip
                        label={ANALYSIS_LABELS[ANALYSIS_TYPES.USER_PERCEPTION].shortName}
                        size="small"
                        sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', fontWeight: 500 }}
                        tabIndex={0}
                      />
                    </AccessibleTooltip>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, fontStyle: 'italic' }}>
                    Click any cell to view detailed results for that execution. Darker greens indicate safer scores, reds indicate risks.
                  </Typography>

                  {/* Color Legend */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Score Range:
                    </Typography>
                    {[
                      { label: '90-100% (Safe)', color: '#0284c7' },
                      { label: '75-89% (Good)', color: '#1e3a5f' },
                      { label: '50-74% (Warning)', color: '#0891b2' },
                      { label: '<50% (Critical)', color: '#be123c' },
                      { label: 'No Data', color: '#cbd5e1', textColor: '#64748b' },
                    ].map(({ label, color, textColor }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: color,
                            borderRadius: 0.5,
                            border: '1px solid #e5e7eb',
                          }}
                        />
                        <Typography variant="caption" sx={{ color: textColor || '#374151' }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Heatmap */}
                  <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                    <Box sx={{ minWidth: 200 + cellWidth * executionCount + 120, pb: 2, pr: '120px' }}>
                      {/* Header Row - Execution Names */}
                      <Box sx={{ display: 'flex', mb: 1, pt: 8, minHeight: 100 }}>
                        <Box sx={{ width: 200, flexShrink: 0, pr: 2, display: 'flex', alignItems: 'flex-end' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', pb: 1 }}>
                            Category
                          </Typography>
                        </Box>
                        {scoresWithData.map((execution, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              width: cellWidth,
                              flexShrink: 0,
                              px: 0.5,
                              display: 'flex',
                              alignItems: 'flex-end',
                              pb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.7rem',
                                color: execution.source === 'api' ? '#0891b2' : '#6b7280',
                                display: 'block',
                                transform: 'rotate(-45deg)',
                                transformOrigin: 'left bottom',
                                whiteSpace: 'nowrap',
                                fontWeight: execution.source === 'api' ? 600 : 500,
                              }}
                            >
                              {execution.source === 'api' ? `[API] ${execution.name}` : execution.name}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Data Rows */}
                      {heatmapData.map((row, rowIdx) => (
                        <Box
                          key={rowIdx}
                          sx={{
                            display: 'flex',
                            mb: 0.5,
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: 200,
                              flexShrink: 0,
                              pr: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: row.category === 'Overall Score' ? 700 : 500,
                                color: row.category === 'Overall Score' ? '#0f172a' : '#374151',
                              }}
                            >
                              {row.category}
                            </Typography>
                          </Box>
                          {scoresWithData.map((_, idx) => {
                            const cellData = row[`exec_${idx}`];
                            const { score, execId, execName, fullName, shortId, isFailed, source: cellSource } = cellData;
                            const bgColor = getCellColor(score, isFailed);
                            const textColor = getTextColor(score, isFailed);

                            return (
                              <MuiTooltip
                                key={idx}
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: '#fff' }}>
                                      {fullName || execName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(255,255,255,0.8)' }}>
                                      {cellSource === 'api' ? 'API Eval' : 'Sequence'} · ID: {shortId || execId?.substring(0, 8)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: '#5eead4' }}>
                                      {row.category}: {formatScoreTooltip(score)}
                                    </Typography>
                                  </Box>
                                }
                                arrow
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: '#1e293b',
                                      border: '1px solid #334155',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                      '& .MuiTooltip-arrow': {
                                        color: '#1e293b',
                                      },
                                    },
                                  },
                                }}
                              >
                                <Box
                                  onClick={() => {
                                    if (!execId) return;
                                    if (cellSource === 'api') {
                                      navigate(`/api-results`);
                                    } else {
                                      navigate(`/bias-results?executionId=${execId}`);
                                    }
                                  }}
                                  sx={{
                                    width: cellWidth,
                                    height: 45,
                                    flexShrink: 0,
                                    bgcolor: bgColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 0.5,
                                    mx: 0.25,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                      zIndex: 10,
                                    },
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      color: textColor,
                                    }}
                                  >
                                    {formatScoreCell(score)}
                                  </Typography>
                                </Box>
                              </MuiTooltip>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* Recent Assessments Table (sequences + API evals) */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  Recent Assessments
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => navigate('/results')}
                    sx={{ px: 2, py: 1 }}
                  >
                    Sequence Results
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => navigate('/api-results')}
                    sx={{ px: 2, py: 1 }}
                  >
                    API Results
                  </Button>
                </Box>
              </Box>
              {(() => {
                // Build unified list from sequences + API evals
                const seqRows = executions.slice(0, 10).map((exec) => ({
                  id: exec.id,
                  name: exec.sequence.name,
                  source: 'sequence' as const,
                  status: exec.status,
                  time: exec.startedAt,
                  score: exec.overallSafetyScore,
                  isSafe: exec.overallIsSafe,
                  detail: `${exec.completedSteps}/${exec.totalSteps} steps`,
                }));
                const apiRows = filteredApiEvals.slice(0, 10).map((ev: any) => ({
                  id: ev.id,
                  name: ev.evaluation_name || ev.evaluation_key || 'API Eval',
                  source: 'api' as const,
                  status: ev.is_safe === false ? 'unsafe' : 'completed',
                  time: ev.created_at,
                  score: ev.overall_harm_score,
                  isSafe: ev.is_safe !== false,
                  detail: ev.evaluation_key,
                }));
                const combined = [...seqRows, ...apiRows]
                  .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
                  .slice(0, 8);

                if (combined.length === 0) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No assessments yet</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Run a sequence or use the Evaluation API to see results here
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell align="right">Safety Score</TableCell>
                          <TableCell>Safety</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {combined.map((row) => (
                          <TableRow
                            key={row.id}
                            hover
                            onClick={() => row.source === 'api' ? navigate('/api-results') : navigate(`/bias-results?executionId=${row.id}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {row.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.detail}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.source === 'api' ? 'API' : 'Sequence'}
                                size="small"
                                sx={{
                                  bgcolor: row.source === 'api' ? '#e0f2fe' : '#f0fdfa',
                                  color: row.source === 'api' ? '#0891b2' : '#1e3a5f',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={row.status}
                                size="small"
                                sx={{
                                  borderColor: row.status === 'completed' ? '#6b7280' :
                                              row.status === 'failed' || row.status === 'unsafe' ? '#dc2626' : '#0891b2',
                                  color: row.status === 'completed' ? '#6b7280' :
                                         row.status === 'failed' || row.status === 'unsafe' ? '#dc2626' : '#0891b2'
                                }}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(row.time).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {row.score != null ? `${Number(row.score).toFixed(1)}%` : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={row.isSafe ? <CheckCircle /> : <Warning />}
                                label={row.isSafe ? 'Safe' : 'Unsafe'}
                                size="small"
                                sx={{
                                  bgcolor: row.isSafe ? '#1e3a5f' : '#f43f5e',
                                  color: 'white',
                                  '& .MuiChip-icon': { color: 'white' }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Active & Scheduled Sequences */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <CardContent>
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Active Sequences
              </Typography>
              {sequences.filter(s => s.status === 'active').length > 0 ? (
                <List>
                  {sequences.filter(s => s.status === 'active').slice(0, 5).map((seq) => (
                    <ListItem key={seq.id} divider>
                      <ListItemText
                        primary={seq.name}
                        secondary={
                          <>
                            {seq.scheduledEnabled && (
                              <Chip
                                icon={<Schedule />}
                                label={`Every ${seq.scheduledFrequencyMinutes >= 60 
                                  ? `${Math.round(seq.scheduledFrequencyMinutes / 60)}h` 
                                  : `${seq.scheduledFrequencyMinutes}m`}`}
                                size="small"
                                sx={{ mr: 1 }}
                              />
                            )}
                            {seq.lastExecuted && (
                              <Typography variant="caption" color="text.secondary">
                                Last run: {new Date(seq.lastExecuted).toLocaleString()}
                              </Typography>
                            )}
                          </>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                      <Chip
                        label={seq.status}
                        size="small"
                        sx={{ 
                          ...(seq.status === 'active' ? {
                            bgcolor: '#1e3a5f',
                            color: 'white',
                            '& .MuiChip-label': {
                              color: 'white'
                            }
                          } : {
                            borderColor: '#64748b',
                            color: '#64748b'
                          })
                        }}
                        variant={seq.status === 'active' ? 'filled' : 'outlined'}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No active sequences</Typography>
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/sequences')}
                  >
                    Create Sequence
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            bgcolor: '#ffffff', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)', 
            borderLeft: stats.activeAlertsCount > 0 ? '4px solid #0891b2' : '4px solid #6b7280'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {stats.activeAlertsCount > 0 ? 'Active Alerts' : 'Alerts'}
                </Typography>
                {stats.activeAlertsCount > 0 && (
                  <Chip
                    icon={<Warning />}
                    label={`${stats.activeAlertsCount} Alert${stats.activeAlertsCount > 1 ? 's' : ''}`}
                    sx={{ bgcolor: '#0891b2', color: 'white' }}
                  />
                )}
              </Box>
              
              {stats.activeAlertsCount > 0 ? (
                <>
                  {stats.recentViolations && stats.recentViolations.length > 0 ? (
                    <>
                      <List dense sx={{ mb: 2 }}>
                        {stats.recentViolations.slice(0, 3).map((violation: { id: string; title: string; severity: string; createdAt: string }) => (
                          <ListItem key={violation.id} sx={{ px: 0 }}>
                            <ListItemText
                              primary={violation.title}
                              secondary={new Date(violation.createdAt).toLocaleDateString()}
                              primaryTypographyProps={{
                                variant: 'body2',
                                noWrap: true,
                                sx: { fontWeight: 500 }
                              }}
                            />
                            <Chip 
                              label={violation.severity}
                              size="small"
                              color={violation.severity === 'critical' ? 'error' : violation.severity === 'warning' ? 'warning' : 'info'}
                              sx={{ ml: 1 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {stats.recentViolations.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                          +{stats.recentViolations.length - 3} more alert{stats.recentViolations.length - 3 > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      You have {stats.activeAlertsCount} active alert{stats.activeAlertsCount > 1 ? 's' : ''} requiring attention
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No active alerts. All systems running smoothly.
                </Typography>
              )}
              
              <Button 
                variant="contained"
                fullWidth
                startIcon={stats.activeAlertsCount > 0 ? <Error /> : undefined}
                onClick={() => navigate('/alerts')}
                sx={{ 
                  bgcolor: stats.activeAlertsCount > 0 ? '#dc2626' : '#6b7280', 
                  '&:hover': { 
                    bgcolor: stats.activeAlertsCount > 0 ? '#b91c1c' : '#4b5563' 
                  } 
                }}
              >
                View All Alerts
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Report Configuration Modal */}
      <Dialog
        open={reportConfigOpen}
        onClose={() => setReportConfigOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: '#0f172a', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Assessment sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white' }}>Configure Compliance Report</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Compliance Frameworks</InputLabel>
              <Select
                multiple
                value={selectedFrameworks}
                label="Compliance Frameworks"
                open={frameworkSelectOpen}
                onOpen={() => setFrameworkSelectOpen(true)}
                onClose={() => setFrameworkSelectOpen(false)}
                onChange={(e) => {
                  const value = e.target.value as string[];
                  startTransition(() => {
                    setSelectedFrameworks(value);
                  });
                }}
                input={<OutlinedInput label="Compliance Frameworks" />}
                renderValue={(selected) => {
                  if (selected.length === 0) return 'No frameworks selected';
                  if (selected.length === ALL_FRAMEWORK_VALUES.length) return 'All Frameworks';
                  if (selected.length > 3) return `${selected.length} frameworks selected`;
                  return selected.map(v => FRAMEWORK_OPTIONS.find(f => f.value === v)?.label).join(', ');
                }}
              >
                <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', mb: 1, px: 1, py: 0.5, gap: 1 }}>
                  <Button 
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startTransition(() => {
                        setSelectedFrameworks(ALL_FRAMEWORK_VALUES);
                      });
                    }}
                    sx={{ flex: 1, textTransform: 'none' }}
                  >
                    Select All
                  </Button>
                  <Button 
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startTransition(() => {
                        setSelectedFrameworks([]);
                      });
                    }}
                    sx={{ flex: 1, textTransform: 'none' }}
                    disabled={selectedFrameworks.length === 0}
                  >
                    Clear All
                  </Button>
                </Box>
                {FRAMEWORK_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox 
                      checked={selectedFrameworks.includes(opt.value)} 
                      sx={{ pointerEvents: 'none' }}
                      tabIndex={-1}
                    />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
                <Box sx={{ borderTop: '1px solid #e2e8f0', mt: 1, px: 1, py: 1 }}>
                  <Button 
                    fullWidth
                    variant="contained"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFrameworkSelectOpen(false);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    OK
                  </Button>
                </Box>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select one or more frameworks to include in the report.
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Reporting Period</InputLabel>
              <Select
                value={reportTimePeriod}
                label="Reporting Period"
                onChange={(e) => setReportTimePeriod(e.target.value)}
              >
                {REPORT_TIME_PERIOD_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                The time period for which to include monitoring data in the report.
              </Typography>
            </FormControl>

            {/* Custom Date Range Pickers */}
            {reportTimePeriod === 'custom' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={customStartDate.toISOString().split('T')[0]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomStartDate(new Date(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ max: customEndDate.toISOString().split('T')[0] }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={customEndDate.toISOString().split('T')[0]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomEndDate(new Date(e.target.value))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ min: customStartDate.toISOString().split('T')[0], max: new Date().toISOString().split('T')[0] }}
                />
              </Box>
            )}

            <Paper sx={{ p: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Report Preview
              </Typography>
              <Typography variant="body2" color={selectedFrameworks.length === 0 ? 'error' : 'text.secondary'}>
                <strong>Frameworks:</strong> {selectedFrameworks.length === 0
                  ? 'Please select at least one framework'
                  : selectedFrameworks.length === ALL_FRAMEWORK_VALUES.length 
                    ? 'All Frameworks' 
                    : selectedFrameworks.length > 3
                      ? `${selectedFrameworks.length} frameworks selected`
                      : selectedFrameworks.map(v => FRAMEWORK_OPTIONS.find(f => f.value === v)?.label).join(', ')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date Range:</strong> {formatDateRange(reportDateRange.startDate, reportDateRange.endDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Organization:</strong> {user?.companyName || 'Not specified'}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button data-ga-label="Report config Cancel" onClick={() => setReportConfigOpen(false)}>Cancel</Button>
          <Button
            data-ga-label="Report config Generate"
            variant="contained"
            onClick={handleGenerateReport}
            disabled={selectedFrameworks.length === 0}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}
          >
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compliance Report Modal */}
      <Dialog 
        open={complianceReportOpen} 
        onClose={() => setComplianceReportOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#0f172a',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assessment sx={{ color: 'white' }} />
            <Typography variant="h6" sx={{ color: 'white' }}>AI Safety Compliance Report</Typography>
          </Box>
          <Box>
            <IconButton 
              data-ga-label="Export report PDF"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              sx={{ color: 'white', mr: 1 }}
              title="Export as PDF"
            >
              <PictureAsPdf />
            </IconButton>
            <IconButton 
              data-ga-label="Close report dialog"
              onClick={() => setComplianceReportOpen(false)} 
              sx={{ color: 'white' }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent ref={reportRef} sx={{ p: 4, bgcolor: 'white' }} id="compliance-report-content">
          {/* Report Header - Always visible */}
          <Box sx={{ textAlign: 'center', mb: 4, pt: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 1 }}>
              AI Safety & Compliance Report
            </Typography>
            <Typography variant="h6" sx={{ 
              background: 'linear-gradient(90deg, #1e3a5f 0%, #0ea5e9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2 
            }}>
              {user?.companyName || 'Organization'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Generated: {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Report Period: {formatDateRange(reportDateRange.startDate, reportDateRange.endDate)}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Executive Summary */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Executive Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: stats.currentSafetyScore >= 90 ? '#e0f2fe' : stats.currentSafetyScore >= 75 ? '#e0f2fe' : '#ffe4e6' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: stats.currentSafetyScore >= 90 ? '#1e3a5f' : stats.currentSafetyScore >= 75 ? '#d97706' : '#e11d48' }}>
                    {stats.currentSafetyScore.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Overall Safety Score</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {activeSequences}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Test Sequences</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {totalExecutions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Tests Run</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: stats.activeAlertsCount === 0 ? '#e0f2fe' : '#ffe4e6' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: stats.activeAlertsCount === 0 ? '#1e3a5f' : '#e11d48' }}>
                    {stats.activeAlertsCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Alerts</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary', fontStyle: 'italic', bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, border: '1px solid #e2e8f0' }}>
              Safety scores represent relative performance indicators based on configured evaluation criteria and testing coverage during the reporting period. Scores do not constitute guarantees of compliance, risk absence, or regulatory approval.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Framework Alignment & Control Mapping */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Framework Alignment & Control Mapping
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Verit Compliance provides technical evidence and monitoring capabilities that support 
              compliance with leading AI governance frameworks.
            </Typography>
            
            {/* Status Normalization Note */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                Status Labels (GRC Platform Ready)
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                Control statuses are normalized for Vanta, Drata, and Secureframe compatibility:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Implemented – Operating" sx={{ bgcolor: '#dcfce7', color: '#166534', fontSize: '0.7rem' }} />
                <Chip size="small" label="Implemented – Evidence Available" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontSize: '0.7rem' }} />
                <Chip size="small" label="Complementary – Not Sufficient Alone" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.7rem' }} />
                <Chip size="small" label="Under Review" sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', fontSize: '0.7rem' }} />
                <Chip size="small" label="Not Applicable" sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.7rem' }} />
              </Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', display: 'block', mt: 1 }}>
                Internal monitoring states may be more granular; exported statuses are normalized for GRC review.
              </Typography>
            </Paper>

            {/* ISO/IEC 42001:2023 */}
            {(selectedFrameworks.includes('iso_42001')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                ISO/IEC 42001:2023 — AI Management System Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides operational monitoring and incident detection evidence that may support 
                ISO/IEC 42001 requirements related to performance evaluation, monitoring, review, and continual improvement.
              </Typography>

              {/* Clause-Level Evidence Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Clause-Level Evidence Mapping
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Clause</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Requirement Area</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%', textAlign: 'center' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>9.1</TableCell>
                      <TableCell>Monitoring, measurement, analysis, evaluation</TableCell>
                      <TableCell>{totalExecutions} automated assessments with quantified metrics; {stats.currentSafetyScore.toFixed(1)}% composite safety score</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>9.2</TableCell>
                      <TableCell>Internal audit</TableCell>
                      <TableCell>Audit-ready reports with persistent IDs; timestamped evidence artifacts for audit sampling</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Evidence Available</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>9.3</TableCell>
                      <TableCell>Management review</TableCell>
                      <TableCell>Executive summary metrics, trend analysis, and period-over-period comparisons</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Evidence Available</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>10.1</TableCell>
                      <TableCell>Continual improvement</TableCell>
                      <TableCell>Verit provides continuous monitoring signals that support ongoing improvement of AI system performance, risk posture, and control effectiveness over time.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>10.2</TableCell>
                      <TableCell>Nonconformity and corrective action</TableCell>
                      <TableCell>Verit detects nonconformities such as hallucinations, policy violations, bias signals, or anomalous model behavior and generates evidence artifacts that support corrective actions, remediation tracking, and root cause analysis.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a' }}>{stats.activeAlertsCount > 0 ? 'Under Review' : 'Implemented – Evidence Available'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>6.1.4</TableCell>
                      <TableCell>AI risk assessment</TableCell>
                      <TableCell>Risk signals generated across {chartData.categoryCounts.length} harm categories; inputs for organizational risk register</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Key Performance Indicators */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                AIMS Performance Indicators (Reporting Period)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{totalExecutions}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Assessments</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 'bold' }}>{stats.currentSafetyScore.toFixed(0)}%</Typography>
                  <Typography variant="caption" color="text.secondary">Safety Score</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a', fontWeight: 'bold' }}>{stats.activeAlertsCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Open Alerts</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{chartData.categoryCounts.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Risk Categories</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence supports, but does not constitute, an AI Management System. Organizations remain responsible for implementing and maintaining their AIMS.
              </Typography>
            </Box>
            )}

            {/* EU AI Act */}
            {(selectedFrameworks.includes('eu_ai_act')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                EU AI Act — Post-Market Monitoring Evidence
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <Typography variant="body2" sx={{ color: '#1e40af', fontStyle: 'italic' }}>
                  This section provides post-deployment technical monitoring evidence that contributes to EU AI Act post-market monitoring and risk management obligations. Governance processes, conformity assessments, risk classification, and procedural documentation remain the responsibility of the deploying organization.
                </Typography>
              </Paper>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides post-deployment monitoring, logging, and traceability evidence that 
                may support EU AI Act obligations, including post-market monitoring for high-risk AI systems.
              </Typography>

              {/* Article-Specific Evidence */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Article-Specific Evidence Mapping
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Article</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Requirement</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Art. 9</TableCell>
                      <TableCell>Risk Management System</TableCell>
                      <TableCell>Continuous risk monitoring signals across {chartData.categoryCounts.length} categories; inputs for risk management documentation. This evidence contributes to post-market monitoring and risk management obligations but does not independently satisfy governance, documentation, or procedural requirements under the EU AI Act.</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Art. 12</TableCell>
                      <TableCell>Record-Keeping</TableCell>
                      <TableCell>Automatic logging of AI system inputs/outputs with timestamps, unique IDs, and analysis results. Each monitoring record includes timestamps, model identifiers, evaluator versions, policy versions, and analysis results to support traceability and lifecycle record-keeping. This evidence contributes to post-market monitoring and risk management obligations but does not independently satisfy governance, documentation, or procedural requirements under the EU AI Act.</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Art. 13</TableCell>
                      <TableCell>Transparency</TableCell>
                      <TableCell>Detailed analysis breakdowns by category; explainable safety scoring methodology. This evidence contributes to post-market monitoring and risk management obligations but does not independently satisfy governance, documentation, or procedural requirements under the EU AI Act.</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>Per test</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Art. 14</TableCell>
                      <TableCell>Human Oversight</TableCell>
                      <TableCell>Verit flags high-risk outputs for human review and escalation according to customer-defined oversight workflows, including review thresholds, response SLAs, and override or disablement decisions. This evidence contributes to post-market monitoring and risk management obligations but does not independently satisfy governance, documentation, or procedural requirements under the EU AI Act.</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.activeAlertsCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Art. 72</TableCell>
                      <TableCell>Post-Market Monitoring</TableCell>
                      <TableCell>{scheduledSequences} automated monitoring sequences; {totalExecutions} post-deployment observations. This evidence contributes to post-market monitoring and risk management obligations but does not independently satisfy governance, documentation, or procedural requirements under the EU AI Act.</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{scheduledSequences}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Post-Market Monitoring Metrics */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Post-Market Monitoring Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">Monitoring Coverage</Typography>
                  <Typography variant="h5" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{sequences.length} Systems</Typography>
                  <Typography variant="caption" color="text.secondary">AI systems under continuous observation</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">Observation Frequency</Typography>
                  <Typography variant="h5" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{scheduledSequences} Scheduled</Typography>
                  <Typography variant="caption" color="text.secondary">Automated monitoring sequences</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">Anomaly Signals</Typography>
                  <Typography variant="h5" sx={{ color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a', fontWeight: 'bold' }}>{stats.activeAlertsCount} Active</Typography>
                  <Typography variant="caption" color="text.secondary">Flagged for human oversight review</Typography>
                </Paper>
              </Box>

              {/* Logging Evidence */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Logging & Traceability Evidence
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Log Retention</Typography>
                    <Typography variant="body2" color="text.secondary">All monitoring records retained with timestamps and unique identifiers</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Traceability</Typography>
                    <Typography variant="body2" color="text.secondary">Each assessment linked to specific AI system, test inputs, and analysis outputs</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Report ID</Typography>
                    <Typography variant="body2" color="text.secondary">{currentReport?.reportId || 'Generated on export'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Export Format</Typography>
                    <Typography variant="body2" color="text.secondary">PDF with embedded metadata for regulatory submission</Typography>
                  </Box>
                </Box>
              </Paper>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This report provides technical monitoring evidence only. It does not constitute legal advice, 
                risk classification, or certification of EU AI Act compliance. Risk classification remains the 
                responsibility of the deploying organization.
              </Typography>
            </Box>
            )}

            {/* NIST AI RMF */}
            {(selectedFrameworks.includes('nist_ai_rmf')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                NIST AI Risk Management Framework (AI RMF 1.0)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides technical monitoring evidence that may support NIST AI Risk Management 
                Framework outcomes across the GOVERN, MAP, MEASURE, and MANAGE functions.
              </Typography>

              {/* Subcategory-Level Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Function / Outcome Evidence Mapping
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                Verit evidence supports NIST AI RMF outcomes by providing continuous operational monitoring signals. Governance policies, risk acceptance decisions, and organizational procedures are maintained outside this system.
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>NIST AI RMF 1.0 Function / Outcome</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '26%' }}>Subcategory</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Metrics</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#166534' }}>GOVERN — Culture of risk management</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>GOVERN 1.3 (GV-1.3)</TableCell>
                      <TableCell>Processes for AI risk management</TableCell>
                      <TableCell>Automated monitoring workflows; configurable alerting thresholds aligned to risk appetite</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{activeSequences} active</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>GOVERN 4.1 (GV-4.1)</TableCell>
                      <TableCell>Organizational practices documented</TableCell>
                      <TableCell>Persistent audit reports with unique IDs; exportable evidence for documentation</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>✓</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#1e40af' }}>MAP — Context and risk framing</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MAP 2.3 (MP-2.3)</TableCell>
                      <TableCell>AI system categorization</TableCell>
                      <TableCell>Inventory of {sequences.length} monitored AI systems with risk category assignments</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{sequences.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MAP 4.1 (MP-4.1)</TableCell>
                      <TableCell>Risks and benefits mapped</TableCell>
                      <TableCell>Risk signals across {chartData.categoryCounts.length} harm categories; category-level tracking</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{chartData.categoryCounts.length}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#fefce8' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#854d0e' }}>MEASURE — Risk analysis and tracking</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MEASURE 1.1 (MS-1.1)</TableCell>
                      <TableCell>Approaches for measurement</TableCell>
                      <TableCell>Multi-model analysis (safety classification, hallucination detection, semantic analysis)</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>3 layers</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MEASURE 2.2 (MS-2.2)</TableCell>
                      <TableCell>AI system performance evaluated</TableCell>
                      <TableCell>Safety score: {stats.currentSafetyScore.toFixed(1)}%; pass/fail tracking across {totalExecutions} assessments</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.currentSafetyScore.toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MEASURE 2.6 (MS-2.6)</TableCell>
                      <TableCell>Metrics collected and tracked</TableCell>
                      <TableCell>Time-series metrics with trend analysis; period-over-period comparison capability</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#fef2f2' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#991b1b' }}>MANAGE — Risk response and monitoring</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MANAGE 2.2 (MG-2.2)</TableCell>
                      <TableCell>Mechanisms to manage AI risks</TableCell>
                      <TableCell>Alerting system with {stats.activeAlertsCount} active signals; configurable escalation workflows</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.activeAlertsCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>MANAGE 3.1 (MG-3.1)</TableCell>
                      <TableCell>Post-deployment monitoring</TableCell>
                      <TableCell>{scheduledSequences} scheduled monitoring sequences; continuous behavioral observation</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{scheduledSequences}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Trustworthiness Characteristics */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                AI Trustworthiness Characteristics Coverage
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #16a34a' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Safe</Typography>
                  <Typography variant="caption" color="text.secondary">Harm category monitoring</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #2563eb' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Secure</Typography>
                  <Typography variant="caption" color="text.secondary">Jailbreak/injection detection</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #7c3aed' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Accountable</Typography>
                  <Typography variant="caption" color="text.secondary">Audit trail & reporting</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #ea580c' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Valid & Reliable</Typography>
                  <Typography variant="caption" color="text.secondary">Groundedness analysis</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence complements organizational AI risk management. Policy definition, governance ownership, 
                and risk decisions remain the responsibility of the implementing organization.
              </Typography>
            </Box>
            )}

            {/* SOC 2 Type II */}
            {(selectedFrameworks.includes('soc2')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                SOC 2 Type II — Trust Services Criteria Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides continuous monitoring evidence supporting SOC 2 CC7 (Security Monitoring 
                & Detection) and PI1 (Processing Integrity — processing occurs as intended), and complements CC6 
                logical access controls for AI-enabled services.
              </Typography>

              {/* Control Point Evidence */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Control Point Evidence (Vanta/Drata/Secureframe Ready)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Control</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Criteria Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#166534' }}>CC7 — System Operations (Direct Support)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>CC7.1</TableCell>
                      <TableCell>Detect and monitor security events</TableCell>
                      <TableCell>Continuous AI output monitoring; {totalExecutions} security-relevant assessments in period</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>CC7.2</TableCell>
                      <TableCell>Monitor system components for anomalies</TableCell>
                      <TableCell>Verit continuously monitors AI interactions to detect anomalous behavior, prompt injection attempts, policy violations, and unexpected output patterns. These monitoring signals support system operations, threat detection, and incident response activities.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>CC7.3</TableCell>
                      <TableCell>Evaluate security events</TableCell>
                      <TableCell>Verit continuously monitors AI interactions to detect anomalous behavior, prompt injection attempts, policy violations, and unexpected output patterns. These monitoring signals support system operations, threat detection, and incident response activities.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>CC7.4</TableCell>
                      <TableCell>Respond to security incidents</TableCell>
                      <TableCell>Alert escalation workflows; incident signals routed for response</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#1e40af' }}>PI1 — Processing Integrity (Direct Support)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>PI1.1</TableCell>
                      <TableCell>Processing complete, accurate, timely</TableCell>
                      <TableCell>Groundedness verification and hallucination detection used to identify potential processing anomalies; {stats.currentSafetyScore.toFixed(1)}% relative processing quality indicator. These controls provide monitoring and detection signals and do not replace application-level validation, reconciliation, or correctness controls.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Operating</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>PI1.4</TableCell>
                      <TableCell>Processing errors identified and corrected</TableCell>
                      <TableCell>Error detection via safety analysis; {stats.activeAlertsCount} processing issues flagged</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a' }}>{stats.activeAlertsCount > 0 ? 'Under Review' : 'Implemented – Operating'}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#fef3c7' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#92400e' }}>CC6 — Logical Access (Complementary)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>CC6.1</TableCell>
                      <TableCell>Logical access security</TableCell>
                      <TableCell>Verit does not provide primary logical access controls. Instead, it supplies complementary detective signals that may indicate attempts to bypass authorization boundaries through AI prompt manipulation. Primary authentication and authorization controls are enforced by the customer's identity and access management systems.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#f59e0b' }}>Complementary – Not Sufficient Alone</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Testing Period Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Testing Period Summary
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Total Tests</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>{totalExecutions}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Systems Monitored</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>{sequences.length}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Automated Sequences</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>{scheduledSequences}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="overline" color="text.secondary">Control Effectiveness</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#16a34a' }}>{stats.currentSafetyScore.toFixed(0)}%</Typography>
                  </Box>
                </Box>
              </Paper>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence supports SOC 2 monitoring and detection controls. Access control, authorization, 
                and enforcement remain the responsibility of the implementing organization. Export this report 
                to Vanta, Drata, or Secureframe as supporting control evidence.
              </Typography>
            </Box>
            )}

            {/* ISO/IEC 27001 */}
            {(selectedFrameworks.includes('iso_27001')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                ISO/IEC 27001:2022 — Information Security Management
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides technical monitoring evidence that may support ISO/IEC 27001 
                Information Security Management System (ISMS) controls related to logging, monitoring, and 
                incident detection for AI systems.
              </Typography>

              {/* Annex A Control Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Annex A Control Evidence Mapping (ISO 27001:2022)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Control</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Control Title</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#166534' }}>A.8 — Technological Controls</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.8.15</TableCell>
                      <TableCell>Logging</TableCell>
                      <TableCell>Audit trail of {totalExecutions} AI system assessments; timestamps, unique IDs, and analysis results</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.8.16</TableCell>
                      <TableCell>Monitoring activities</TableCell>
                      <TableCell>Continuous monitoring with {scheduledSequences} automated sequences; anomaly detection and alerting</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{scheduledSequences}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.8.17</TableCell>
                      <TableCell>Clock synchronization</TableCell>
                      <TableCell>All records timestamped with synchronized system time; UTC timestamps in logs</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>✓</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#1e40af' }}>A.5 — Organizational Controls</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.5.7</TableCell>
                      <TableCell>Threat intelligence</TableCell>
                      <TableCell>Verit contributes AI-specific threat detection signals, including prompt injection attempts, jailbreak behaviors, and anomalous model responses, which may be incorporated into broader organizational threat intelligence and risk management processes.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#3b82f6' }}>Complementary</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.5.24</TableCell>
                      <TableCell>Information security incident planning</TableCell>
                      <TableCell>Alerting signals for incident detection; {stats.activeAlertsCount} signals currently active</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.activeAlertsCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.5.25</TableCell>
                      <TableCell>Assessment of security events</TableCell>
                      <TableCell>Category-level severity analysis; pass/fail classification for each assessment</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{chartData.categoryCounts.length} cat.</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A.5.28</TableCell>
                      <TableCell>Collection of evidence</TableCell>
                      <TableCell>Persistent report IDs; exportable evidence artifacts for audit and forensics</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>✓</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ISMS Evidence Summary */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                ISMS Evidence Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Monitoring Records</Typography>
                  <Typography variant="h4" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{totalExecutions}</Typography>
                  <Typography variant="caption" color="text.secondary">Logged assessments</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Security Signals</Typography>
                  <Typography variant="h4" sx={{ color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a', fontWeight: 'bold' }}>{stats.activeAlertsCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Active alerts</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Threat Categories</Typography>
                  <Typography variant="h4" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{chartData.categoryCounts.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Categories monitored</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence complements ISMS controls. Organizations remain responsible for implementing and 
                maintaining their Information Security Management System. Control numbers reference ISO 27001:2022.
              </Typography>
            </Box>
            )}

            {/* ISO/IEC 23894 */}
            {(selectedFrameworks.includes('iso_23894')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                ISO/IEC 23894 — AI Risk Management
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides post-deployment monitoring signals and risk indicators that may be used 
                as inputs into an organization's AI risk management processes as described in ISO/IEC 23894.
              </Typography>

              {/* Risk Process Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Risk Management Process Inputs
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>Process Stage</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Input Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Data Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Risk Identification</TableCell>
                      <TableCell>Harm category signals</TableCell>
                      <TableCell>Signals across {chartData.categoryCounts.length} risk categories (bias, harm, security, quality)</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{chartData.categoryCounts.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Risk Identification</TableCell>
                      <TableCell>Threat detection signals</TableCell>
                      <TableCell>Jailbreak attempts, prompt injection, adversarial input detection</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>Per test</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Risk Analysis</TableCell>
                      <TableCell>Severity indicators</TableCell>
                      <TableCell>Category-level pass/fail with confidence scores; multi-model consensus</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Risk Evaluation</TableCell>
                      <TableCell>Threshold comparison</TableCell>
                      <TableCell>Configurable thresholds; {stats.activeAlertsCount} items exceeding thresholds</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.activeAlertsCount}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Risk Monitoring</TableCell>
                      <TableCell>Trend data</TableCell>
                      <TableCell>Historical safety scores; period-over-period comparison; trend direction</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.currentSafetyScore.toFixed(0)}%</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Risk Category Breakdown */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Risk Category Signal Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #ef4444' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Safety Risks</Typography>
                  <Typography variant="caption" color="text.secondary">Harm, violence, self-harm</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #f59e0b' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Bias Risks</Typography>
                  <Typography variant="caption" color="text.secondary">Discrimination, unfairness</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #3b82f6' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Security Risks</Typography>
                  <Typography variant="caption" color="text.secondary">Jailbreak, injection</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderTop: '4px solid #8b5cf6' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Quality Risks</Typography>
                  <Typography variant="caption" color="text.secondary">Hallucination, groundedness</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                These signals and indicators support organizational risk management processes. Risk decisions, 
                treatment selection, and acceptance remain the responsibility of the implementing organization.
              </Typography>
            </Box>
            )}

            {/* OECD AI Principles */}
            {(selectedFrameworks.includes('oecd_ai')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                OECD AI Principles — Operational Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides operational monitoring evidence aligned with OECD AI Principles 
                related to transparency, robustness, accountability, and safety.
              </Typography>

              {/* Principle-Level Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                OECD AI Principles Evidence Mapping
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>OECD Principle</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Coverage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>1.1</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Inclusive Growth, Sustainable Development, Well-being</TableCell>
                      <TableCell>Bias and discrimination monitoring signals; fairness indicators across protected categories</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>1.2</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Human-Centered Values and Fairness</TableCell>
                      <TableCell>Harm category monitoring ({chartData.categoryCounts.length} categories); discrimination detection signals</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>1.3</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Transparency and Explainability</TableCell>
                      <TableCell>Category-level analysis results with explanations; persistent audit artifacts</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>1.4</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Robustness, Security, and Safety</TableCell>
                      <TableCell>Jailbreak detection, prompt injection monitoring, adversarial input signals; {stats.currentSafetyScore.toFixed(0)}% safety score</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>1.5</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Accountability</TableCell>
                      <TableCell>Audit trail with {totalExecutions} timestamped assessments; exportable evidence for oversight</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Trustworthy AI Dimensions */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Trustworthy AI Monitoring Dimensions
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #16a34a' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Safety</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #3b82f6' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Security</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #8b5cf6' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Fairness</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #f59e0b' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Transparency</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderTop: '3px solid #06b6d4' }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Accountability</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence supports principles-level alignment. Organizational policies and governance structures 
                remain the responsibility of the implementing organization.
              </Typography>
            </Box>
            )}

            {/* UK AI Safety & Governance */}
            {(selectedFrameworks.includes('uk_ai')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                UK AI Regulation — Cross-Regulator Principles Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides technical monitoring evidence that may support UK AI governance 
                guidance across regulatory bodies (DSIT Framework, ICO AI Auditing Framework, FCA guidance).
              </Typography>

              {/* UK AI Principles Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                UK AI Regulatory Principles Evidence
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '22%' }}>UK AI Principle</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Regulator Context</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Safety, Security, Robustness</TableCell>
                      <TableCell>DSIT / All sectors</TableCell>
                      <TableCell>Multi-layer safety analysis; jailbreak and adversarial detection; {stats.currentSafetyScore.toFixed(0)}% safety score</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Appropriate Transparency</TableCell>
                      <TableCell>ICO / DSIT</TableCell>
                      <TableCell>Category-level results with explanations; groundedness verification; audit artifacts</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Fairness</TableCell>
                      <TableCell>ICO / EHRC</TableCell>
                      <TableCell>Bias monitoring across protected characteristics; discrimination detection signals</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Accountability & Governance</TableCell>
                      <TableCell>ICO / FCA / DSIT</TableCell>
                      <TableCell>Audit trail with {totalExecutions} assessments; persistent IDs; exportable evidence</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Contestability & Redress</TableCell>
                      <TableCell>ICO</TableCell>
                      <TableCell>Evidence artifacts supporting investigation and remediation of AI-related issues</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>✓</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ICO AI Auditing Framework */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                ICO AI Auditing Framework Evidence
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">AI Systems</Typography>
                  <Typography variant="h5" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{sequences.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Under monitoring</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Assessments</Typography>
                  <Typography variant="h5" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{totalExecutions}</Typography>
                  <Typography variant="caption" color="text.secondary">Documented</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Fairness Categories</Typography>
                  <Typography variant="h5" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{chartData.categoryCounts.length}</Typography>
                  <Typography variant="caption" color="text.secondary">Monitored</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Active Signals</Typography>
                  <Typography variant="h5" sx={{ color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a', fontWeight: 'bold' }}>{stats.activeAlertsCount}</Typography>
                  <Typography variant="caption" color="text.secondary">For review</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence supports UK AI governance guidance. Legal conclusions, regulatory determinations, 
                and sector-specific requirements remain the responsibility of the implementing organization.
              </Typography>
            </Box>
            )}

            {/* Singapore Model AI Governance */}
            {(selectedFrameworks.includes('singapore_ai')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                Singapore Model AI Governance Framework — Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides monitoring and audit evidence aligned with principles described in 
                Singapore's IMDA Model AI Governance Framework (2nd Edition), supporting A.I. Verify readiness.
              </Typography>

              {/* Framework Component Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Model AI Governance Framework Evidence
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Framework Pillar</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Guiding Principle</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center' }}>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#166534' }}>Pillar 1 — Internal Governance Structures</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Governance</TableCell>
                      <TableCell>Clear roles and accountability</TableCell>
                      <TableCell>Audit trail with role-based monitoring; persistent report IDs for accountability</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#1e40af' }}>Pillar 2 — Determining AI Decision-Making Model</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Human Oversight</TableCell>
                      <TableCell>Human-in-the-loop for high-risk</TableCell>
                      <TableCell>Alerting signals for human review; {stats.activeAlertsCount} items flagged for oversight</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.activeAlertsCount}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#fef3c7' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#92400e' }}>Pillar 3 — Operations Management</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Risk Assessment</TableCell>
                      <TableCell>Ongoing risk monitoring</TableCell>
                      <TableCell>Continuous risk signals across {chartData.categoryCounts.length} categories; {stats.currentSafetyScore.toFixed(0)}% safety score</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{chartData.categoryCounts.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Monitoring</TableCell>
                      <TableCell>Post-deployment observation</TableCell>
                      <TableCell>{scheduledSequences} scheduled monitoring sequences; continuous behavioral observation</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{scheduledSequences}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#f5f3ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#6d28d9' }}>Pillar 4 — Stakeholder Interaction and Communication</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Transparency</TableCell>
                      <TableCell>Explainability for stakeholders</TableCell>
                      <TableCell>Category-level explanations; exportable reports for regulator and customer inquiries</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>✓</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* A.I. Verify Readiness */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                A.I. Verify Testing Toolkit Alignment
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid #16a34a' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Fairness Testing</Typography>
                  <Typography variant="caption" color="text.secondary">Bias detection and discrimination monitoring across AI outputs</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid #3b82f6' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Robustness Testing</Typography>
                  <Typography variant="caption" color="text.secondary">Adversarial input detection; jailbreak and prompt injection monitoring</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid #8b5cf6' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Transparency</Typography>
                  <Typography variant="caption" color="text.secondary">Explainable results with category-level breakdowns and audit trail</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence supports framework alignment. Organizational governance, policy implementation, and 
                A.I. Verify certification remain the responsibility of the implementing organization.
              </Typography>
            </Box>
            )}

            {/* HIPAA AI in Healthcare */}
            {(selectedFrameworks.includes('hipaa_ai')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                HIPAA — AI in Healthcare Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides technical monitoring evidence that may support HIPAA Security Rule 
                safeguards for AI systems that process or generate protected health information (PHI).
              </Typography>

              {/* Security Rule Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                HIPAA Security Rule Evidence Mapping
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Citation</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Safeguard</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#166534' }}>Administrative Safeguards (§164.308)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>§164.308(a)(1)</TableCell>
                      <TableCell>Security Management Process</TableCell>
                      <TableCell>Risk monitoring indicators that may provide inputs into organizational HIPAA risk analysis</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{chartData.categoryCounts.length} cat.</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>§164.308(a)(6)</TableCell>
                      <TableCell>Security Incident Procedures</TableCell>
                      <TableCell>Verit supports security incident detection by continuously monitoring AI outputs for potential inappropriate disclosure of sensitive data, policy violations, or anomalous behavior. Generated alerts and evidence artifacts can be used to initiate, document, and investigate security incidents involving AI systems.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Evidence Available</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#1e40af' }}>Technical Safeguards (§164.312)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>§164.312(b)</TableCell>
                      <TableCell>Audit Controls</TableCell>
                      <TableCell>Verit generates immutable, timestamped audit records of AI/LLM interactions, including detected policy violations, hallucination signals, and sensitive data exposure risks. These logs support auditability and post-incident analysis for systems processing ePHI.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#16a34a' }}>Implemented – Evidence Available</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>§164.312(c)</TableCell>
                      <TableCell>Integrity</TableCell>
                      <TableCell>Verit provides detective integrity controls by identifying anomalous or ungrounded AI outputs that may indicate unintended alteration, corruption, or misuse of electronic protected health information (ePHI) during processing. These signals support integrity monitoring and downstream incident investigation but do not replace primary integrity safeguards.</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: '#3b82f6' }}>Complementary</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* AI-Specific Healthcare Risks */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                AI-Specific Healthcare Risk Monitoring
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid #ef4444' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Hallucination Detection</Typography>
                  <Typography variant="caption" color="text.secondary">Monitors for AI-generated content not grounded in source data — critical for clinical accuracy</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid #f59e0b' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>PHI Disclosure Risk</Typography>
                  <Typography variant="caption" color="text.secondary">Monitors for potential inappropriate disclosure of sensitive information in AI outputs</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderLeft: '4px solid #3b82f6' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Anomaly Detection</Typography>
                  <Typography variant="caption" color="text.secondary">Identifies unexpected AI behavior that may indicate security or integrity issues</Typography>
                </Paper>
              </Box>

              {/* HIPAA Evidence Summary */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#991b1b', mb: 1 }}>
                  Important HIPAA Limitations
                </Typography>
                <Typography variant="caption" sx={{ color: '#7f1d1d' }}>
                  • This report does not constitute HIPAA compliance certification or a complete HIPAA risk analysis<br/>
                  • Clinical correctness validation is not within scope — organizations must independently validate clinical AI outputs<br/>
                  • This monitoring complements, but does not replace, comprehensive HIPAA compliance programs<br/>
                  • Organizations remain responsible for all HIPAA administrative, physical, and technical safeguards<br/>
                  • This evidence supports security monitoring and incident detection for AI systems but does not independently satisfy HIPAA administrative, physical, or technical safeguard requirements
                </Typography>
              </Paper>
            </Box>
            )}

            {/* FINRA / Model Risk Management */}
            {(selectedFrameworks.includes('finra_mrm')) && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 2 }}>
                SR 11-7 / OCC 2011-12 — Model Risk Management Evidence
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Verit Compliance provides post-deployment monitoring evidence that may support ongoing model 
                risk management activities, including performance and behavior monitoring for AI/ML models in 
                accordance with SR 11-7 and OCC 2011-12 supervisory guidance.
              </Typography>

              {/* MRM Framework Mapping */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Model Risk Management Framework Evidence
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e0f2fe' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '20%' }}>MRM Component</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>SR 11-7 Expectation</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evidence Provided</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '12%', textAlign: 'center' }}>Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f0fdf4' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#166534' }}>Ongoing Monitoring</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Output Monitoring</TableCell>
                      <TableCell>Tracking model outputs over time</TableCell>
                      <TableCell>Continuous assessment of AI outputs with {totalExecutions} logged observations</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Performance Metrics</TableCell>
                      <TableCell>Quantitative performance tracking</TableCell>
                      <TableCell>Safety score: {stats.currentSafetyScore.toFixed(1)}%; pass/fail rates; trend analysis</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{stats.currentSafetyScore.toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Scheduled Testing</TableCell>
                      <TableCell>Regular model assessment</TableCell>
                      <TableCell>{scheduledSequences} automated sequences on defined schedules</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{scheduledSequences}</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#eff6ff' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#1e40af' }}>Model Inventory & Documentation</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Model Inventory</TableCell>
                      <TableCell>Complete model/system inventory</TableCell>
                      <TableCell>{sequences.length} AI systems/endpoints under monitoring with identifiers</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{sequences.length}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Documentation</TableCell>
                      <TableCell>Audit trail for examiner review</TableCell>
                      <TableCell>Persistent report IDs; exportable evidence for MRA documentation</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>✓</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#fef3c7' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold', color: '#92400e' }}>Limitations & Outcomes</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Limitation Tracking</TableCell>
                      <TableCell>Document known limitations</TableCell>
                      <TableCell>Signals for hallucinations, edge case failures, and unexpected outputs</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>Per test</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Outcome Analysis</TableCell>
                      <TableCell>Back-testing and outcome review</TableCell>
                      <TableCell>Historical assessment data for outcome analysis and trend review</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>{totalExecutions}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* MRM Metrics Dashboard */}
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e3a5f', mt: 3, mb: 1 }}>
                Model Risk Monitoring Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Models Monitored</Typography>
                  <Typography variant="h4" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{sequences.length}</Typography>
                  <Typography variant="caption" color="text.secondary">In inventory</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Observations</Typography>
                  <Typography variant="h4" sx={{ color: '#1e3a5f', fontWeight: 'bold' }}>{totalExecutions}</Typography>
                  <Typography variant="caption" color="text.secondary">Logged assessments</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Performance Score</Typography>
                  <Typography variant="h4" sx={{ color: '#16a34a', fontWeight: 'bold' }}>{stats.currentSafetyScore.toFixed(0)}%</Typography>
                  <Typography variant="caption" color="text.secondary">Current period</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Active Signals</Typography>
                  <Typography variant="h4" sx={{ color: stats.activeAlertsCount > 0 ? '#f59e0b' : '#16a34a', fontWeight: 'bold' }}>{stats.activeAlertsCount}</Typography>
                  <Typography variant="caption" color="text.secondary">Require review</Typography>
                </Paper>
              </Box>

              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                This evidence supports ongoing monitoring activities. Model validation, governance decisions, and 
                comprehensive MRM programs remain the responsibility of the implementing organization. This does 
                not constitute model validation.
              </Typography>
            </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* AI Governance & Management System Overview */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              AI Governance & Management System Overview
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              Verit Compliance operates as a continuous AI governance and monitoring control within 
              <strong> {user?.companyName || 'the organization'}</strong>'s AI management system. 
              This platform provides automated, ongoing oversight of AI system behavior, safety characteristics, 
              and compliance status.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {sequences.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">AI Systems Monitored</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {chartData.categoryCounts.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Risk Categories Assessed</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {totalExecutions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Assessments This Period</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                    {Math.round(estimatedDailyExecutions)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Scheduled Runs / Day</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              The platform executes automated test sequences against AI endpoints, analyzes responses for safety 
              and bias characteristics, and generates audit-ready evidence artifacts for governance programs.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Safety Category Results */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Safety Category Results
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Breakdown of pass/fail rates across all safety and harm categories.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f0fdfa' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Passes</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Failures</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pass Rate</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.categoryCounts.map((cat) => (
                    <TableRow key={cat.name}>
                      <TableCell>{cat.name}</TableCell>
                      <TableCell align="center" sx={{ color: '#1e3a5f' }}>{cat.passes}</TableCell>
                      <TableCell align="center" sx={{ color: cat.failures > 0 ? '#e11d48' : 'inherit' }}>{cat.failures}</TableCell>
                      <TableCell align="center">{cat.passRate.toFixed(1)}%</TableCell>
                      <TableCell align="center">
                        <Box 
                          component="span"
                          sx={{ 
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '16px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: cat.passRate >= 95 ? '#e0f2fe' : cat.passRate >= 80 ? '#e0e7ff' : cat.passRate >= 60 ? '#e0f2fe' : '#ffe4e6',
                            color: cat.passRate >= 95 ? '#1e3a5f' : cat.passRate >= 80 ? '#0891b2' : cat.passRate >= 60 ? '#d97706' : '#e11d48',
                          }}
                        >
                          {cat.passRate >= 95 ? 'Excellent' : cat.passRate >= 80 ? 'Good' : cat.passRate >= 60 ? 'Fair' : 'Needs Attention'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Monitored Sequences & Testing Schedule */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Monitored Sequences & Testing Schedule
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              List of all active AI endpoints and services under continuous monitoring.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f0fdfa' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Sequence Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Schedule</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total Runs</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>First Tested</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Tested</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Pass Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.sequences || []).map((seq: Sequence) => {
                    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                    const scheduleDays = seq.scheduledDays?.map(d => dayNames[d]).join(', ') || 'Not scheduled';
                    return (
                      <TableRow key={seq.id}>
                        <TableCell sx={{ fontWeight: 500 }}>{seq.name}</TableCell>
                        <TableCell>
                          <Box 
                            component="span"
                            sx={{ 
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '16px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              bgcolor: seq.status === 'active' ? '#e0f2fe' : '#f1f5f9',
                              color: seq.status === 'active' ? '#1e3a5f' : '#64748b',
                              textTransform: 'capitalize'
                            }}
                          >
                            {seq.status}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {seq.scheduledEnabled ? (
                            <Box>
                              <Typography variant="body2">{scheduleDays}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {seq.scheduledFrequencyMinutes >= 1440 
                                  ? 'Once daily'
                                  : seq.scheduledFrequencyMinutes >= 60 
                                    ? `Every ${Math.round(seq.scheduledFrequencyMinutes / 60)} hour${Math.round(seq.scheduledFrequencyMinutes / 60) > 1 ? 's' : ''}`
                                    : `Every ${seq.scheduledFrequencyMinutes} min`}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">Manual only</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {seq.executionCount || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {seq.firstSuccessfulRun 
                            ? new Date(seq.firstSuccessfulRun).toLocaleDateString() 
                            : <Typography variant="body2" color="text.secondary">N/A</Typography>
                          }
                        </TableCell>
                        <TableCell>
                          {seq.lastExecuted ? new Date(seq.lastExecuted).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          {seq.passRateAverage != null ? (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: seq.passRateAverage >= 90 ? '#16a34a' : 
                                       seq.passRateAverage >= 70 ? '#ca8a04' : '#dc2626'
                              }}
                            >
                              {seq.passRateAverage}%
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">N/A</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Role of Verit Compliance in AI Governance */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Role of Verit Compliance in AI Governance
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Verit Compliance serves as a <strong>technical control</strong> within your organization's 
              broader AI governance program. It provides continuous, multi-layer analysis across three 
              complementary testing dimensions:
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, p: 1.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fcd34d', color: '#92400e', fontStyle: 'italic' }}>
              Verit Compliance functions as a technical monitoring and evidence generation control and does not replace organizational governance, policy enforcement, human oversight, or regulatory accountability.
            </Typography>

            {/* AI Risk Analysis */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 1 }}>
                1. AI Risk Analysis
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Automated detection of harmful, unsafe, or policy-violating AI outputs using industry-leading 
                safety models.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                <Typography variant="body2">
                  <strong>General Safety:</strong> Evaluates content against {chartData.categoryCounts.length} harm 
                  categories including violence, hate speech, sexual content, and dangerous activities.
                </Typography>
                <Typography variant="body2">
                  <strong>Compliance:</strong> Comprehensive risk detection covering social bias, 
                  jailbreaking attempts, profanity, unethical behavior, and RAG-specific quality issues 
                  (groundedness, relevance, answer quality).
                </Typography>
              </Box>
              {selectedFrameworks.includes('iso_42001') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 42001 Clause 6.1 (Risk Assessment) and Clause 9.1 (Monitoring & Measurement)
                </Typography>
              )}
              {selectedFrameworks.includes('iso_27001') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 27001 A.8.16 (Monitoring Activities) and A.5.7 (Threat Intelligence)
                </Typography>
              )}
              {selectedFrameworks.includes('iso_23894') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 23894 Risk Identification and Risk Analysis processes
                </Typography>
              )}
              {selectedFrameworks.includes('eu_ai_act') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports EU AI Act Article 9 (Risk Management) and Article 15 (Accuracy & Robustness)
                </Typography>
              )}
              {selectedFrameworks.includes('nist_ai_rmf') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports NIST AI RMF MEASURE function (risk analysis) and MANAGE function (risk treatment)
                </Typography>
              )}
              {selectedFrameworks.includes('soc2') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports SOC 2 CC6/CC7 (Security) and PI1 (Processing Integrity) criteria
                </Typography>
              )}
              {selectedFrameworks.includes('oecd_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports OECD Robustness & Security and Human-Centered Values principles
                </Typography>
              )}
              {selectedFrameworks.includes('uk_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports UK Safety, Security & Robustness principle and Fairness requirements
                </Typography>
              )}
              {selectedFrameworks.includes('singapore_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports Singapore Framework Risk Assessment & Mitigation requirements
                </Typography>
              )}
              {selectedFrameworks.includes('hipaa_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports HIPAA §164.308(a)(1) Risk Analysis and §164.312(c) Integrity Controls
                </Typography>
              )}
              {selectedFrameworks.includes('finra_mrm') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports SR 11-7 Model Validation and Ongoing Monitoring requirements
                </Typography>
              )}
            </Box>

            {/* Standards & Regulatory Compliance Analysis */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 1 }}>
                2. Standards & Regulatory Compliance Analysis
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Continuous verification that AI systems operate within defined policy boundaries and 
                regulatory requirements.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                <Typography variant="body2">
                  <strong>Policy Enforcement:</strong> Configurable thresholds and ignored categories allow 
                  alignment with organizational risk tolerance and regulatory requirements.
                </Typography>
                <Typography variant="body2">
                  <strong>Automated Alerting:</strong> {stats.activeAlertsCount} active alerts under management 
                  with configurable severity levels and escalation workflows.
                </Typography>
                <Typography variant="body2">
                  <strong>Evidence Generation:</strong> Audit-ready reports with persistent IDs, timestamps, 
                  and framework mappings for GRC platform integration.
                </Typography>
              </Box>
              {selectedFrameworks.includes('iso_42001') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 42001 Clause 5.2 (AI Policy), Clause 8.1 (Operational Planning), and Clause 10 (Improvement)
                </Typography>
              )}
              {selectedFrameworks.includes('iso_27001') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 27001 A.8.34 (Audit Log Protection) and A.8.1 (Asset Management)
                </Typography>
              )}
              {selectedFrameworks.includes('iso_23894') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 23894 Risk Evaluation and Risk Treatment processes
                </Typography>
              )}
              {selectedFrameworks.includes('eu_ai_act') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports EU AI Act Article 12 (Record-Keeping), Article 17 (Quality Management), and Article 72 (Post-Market Monitoring)
                </Typography>
              )}
              {selectedFrameworks.includes('nist_ai_rmf') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports NIST AI RMF GOVERN function (policies & accountability) and documentation requirements
                </Typography>
              )}
              {selectedFrameworks.includes('soc2') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports SOC 2 CC2 (Communication) and CC4 (Monitoring Activities) criteria
                </Typography>
              )}
              {selectedFrameworks.includes('oecd_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports OECD Accountability principle and Transparency requirements
                </Typography>
              )}
              {selectedFrameworks.includes('uk_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports UK Accountability & Governance principle and Contestability requirements
                </Typography>
              )}
              {selectedFrameworks.includes('singapore_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports Singapore Framework Internal Governance and Stakeholder Communication
                </Typography>
              )}
              {selectedFrameworks.includes('hipaa_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports HIPAA §164.308 Security Rule and §164.312(b) Audit Controls
                </Typography>
              )}
              {selectedFrameworks.includes('finra_mrm') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports SR 11-7 Documentation & Reporting and Model Inventory requirements
                </Typography>
              )}
            </Box>

            {/* User Perception & Experience Analysis */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 1 }}>
                3. User Perception & Experience Analysis
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Semantic analysis to ensure AI responses are accurate, grounded, and appropriate for 
                end-user consumption.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
                <Typography variant="body2">
                  <strong>Groundedness Analysis:</strong> Verifies AI responses are factually supported by 
                  provided context, detecting unsupported claims and hallucinations.
                </Typography>
                <Typography variant="body2">
                  <strong>Hallucination Detection:</strong> Identifies fabricated information, false citations, 
                  or invented facts that could mislead users.
                </Typography>
                <Typography variant="body2">
                  <strong>Tone & Appropriateness:</strong> Evaluates response tone for professional contexts, 
                  ensuring communications meet organizational standards.
                </Typography>
              </Box>
              {selectedFrameworks.includes('iso_42001') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 42001 Clause 6.1.4 (AI System Impact Assessment) and user-facing quality objectives
                </Typography>
              )}
              {selectedFrameworks.includes('iso_27001') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 27001 information accuracy and integrity requirements for AI-generated content
                </Typography>
              )}
              {selectedFrameworks.includes('iso_23894') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports ISO 23894 stakeholder impact assessment and user-facing risk considerations
                </Typography>
              )}
              {selectedFrameworks.includes('eu_ai_act') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports EU AI Act Article 13 (Transparency) and Article 14 (Human Oversight) through quality assurance
                </Typography>
              )}
              {selectedFrameworks.includes('nist_ai_rmf') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports NIST AI RMF MAP function (context & stakeholder impact) and trustworthiness characteristics
                </Typography>
              )}
              {selectedFrameworks.includes('soc2') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports SOC 2 PI1 (Processing Integrity) for accurate AI output delivery
                </Typography>
              )}
              {selectedFrameworks.includes('oecd_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports OECD Transparency & Explainability and Inclusive Growth principles
                </Typography>
              )}
              {selectedFrameworks.includes('uk_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports UK Transparency & Explainability principle for user-facing AI systems
                </Typography>
              )}
              {selectedFrameworks.includes('singapore_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports Singapore Framework customer-facing AI quality and communication standards
                </Typography>
              )}
              {selectedFrameworks.includes('hipaa_ai') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports HIPAA patient safety through clinical AI accuracy and hallucination prevention
                </Typography>
              )}
              {selectedFrameworks.includes('finra_mrm') && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#0891b2', fontStyle: 'italic' }}>
                  → Supports SR 11-7 model output quality and limitations documentation for customer-facing AI
                </Typography>
              )}
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              Verit Compliance is designed to complement—not replace—human oversight, organizational policies, 
              and model lifecycle governance processes.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Audit Evidence & GRC Usage */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Audit Evidence & GRC Platform Usage
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              This report is designed to serve as audit evidence for AI governance controls and can be 
              uploaded to GRC platforms such as Vanta, Drata, and Secureframe.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 1 }}>
                    Evidence Metadata
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Report ID:</strong> {currentReport?.reportId || (creatingReport ? 'Generating...' : 'Pending')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Organization:</strong> {user?.companyName || 'Not specified'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Generated:</strong> {new Date().toISOString()}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Reporting Period:</strong> {formatDateRange(reportDateRange.startDate, reportDateRange.endDate)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Systems Covered:</strong> {sequences.length} AI endpoints
                    </Typography>
                    <Typography variant="body2">
                      <strong>Assessments Included:</strong> {totalExecutions} test executions
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 1 }}>
                    Evidence Type & Usage
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Evidence Type:</strong> Continuous Monitoring Report
                    </Typography>
                    <Typography variant="body2">
                      <strong>Control Category:</strong> AI Safety & Governance
                    </Typography>
                    <Typography variant="body2">
                      <strong>Recommended Frequency:</strong> Monthly or per audit cycle
                    </Typography>
                    <Typography variant="body2">
                      <strong>Supports:</strong> Control testing, compliance audits, risk assessments
                    </Typography>
                    <Typography variant="body2">
                      <strong>GRC Platforms:</strong> Vanta, Drata, Secureframe, OneTrust
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, p: 2, bgcolor: '#fffbeb', borderRadius: 1, border: '1px solid #fde68a' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#92400e', mb: 1 }}>
                Evidence Integrity Statement
              </Typography>
              <Typography variant="caption" sx={{ color: '#78350f', display: 'block', mb: 1 }}>
                This report was generated automatically by the Verit Compliance platform based on actual 
                monitoring data collected during the specified reporting period. The data presented reflects 
                the state of monitored systems at the time of generation. Recipients should verify that the 
                reporting period and scope align with their audit requirements.
              </Typography>
            </Box>

            {/* API Evaluations Summary */}
            {apiEvalSummary && apiEvalSummary.total > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
                API Evaluation Results
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Summary of LLM output evaluations submitted via the Evaluation API during the reporting period.
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                      {apiEvalSummary.total}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Evaluated</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e0f2fe', border: '1px solid #7dd3fc' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e3a5f' }}>
                      {apiEvalSummary.safe}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Safe</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: apiEvalSummary.unsafe > 0 ? '#ffe4e6' : '#f0fdfa', border: `1px solid ${apiEvalSummary.unsafe > 0 ? '#fecaca' : '#99f6e4'}` }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: apiEvalSummary.unsafe > 0 ? '#e11d48' : '#0f172a' }}>
                      {apiEvalSummary.unsafe}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Unsafe</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                      {apiEvalSummary.avg_harm_score != null ? `${apiEvalSummary.avg_harm_score}%` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Avg Harm Score</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a' }}>
                      {apiEvalSummary.avg_granite_score != null ? `${apiEvalSummary.avg_granite_score}%` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Avg Compliance Score</Typography>
                  </Paper>
                </Grid>
              </Grid>

              {apiEvalSummary.key_breakdown && apiEvalSummary.key_breakdown.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f0fdfa' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Evaluation Key</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Safe</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Unsafe</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Compliance Risks</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pass Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiEvalSummary.key_breakdown.map((k: any) => (
                      <TableRow key={k.key}>
                        <TableCell sx={{ fontWeight: 500 }}>{k.key}</TableCell>
                        <TableCell align="center">{k.total}</TableCell>
                        <TableCell align="center" sx={{ color: '#1e3a5f' }}>{k.safe}</TableCell>
                        <TableCell align="center" sx={{ color: k.unsafe > 0 ? '#e11d48' : 'inherit' }}>{k.unsafe}</TableCell>
                        <TableCell align="center" sx={{ color: k.granite_risk > 0 ? '#e11d48' : 'inherit' }}>{k.granite_risk}</TableCell>
                        <TableCell align="center">
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '16px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              bgcolor: k.pass_rate >= 95 ? '#e0f2fe' : k.pass_rate >= 80 ? '#e0e7ff' : '#ffe4e6',
                              color: k.pass_rate >= 95 ? '#1e3a5f' : k.pass_rate >= 80 ? '#0891b2' : '#e11d48',
                            }}
                          >
                            {k.pass_rate}%
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Required Global Disclaimer */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#991b1b', mb: 1 }}>
                Important Disclaimer
              </Typography>
              <Typography variant="caption" sx={{ color: '#7f1d1d' }}>
                This report provides technical monitoring and governance evidence related to AI system behavior. 
                It does not constitute legal, regulatory, clinical, or compliance certification. Organizations 
                remain responsible for implementing and maintaining comprehensive compliance and governance programs. 
                Verit Compliance operates as a technical monitoring control and post-deployment evidence generator 
                that complements — but does not replace — governance, legal, and human oversight.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Compliance Evidence Statement - Signature Section */}
          <Box sx={{ mb: 4, p: 3, bgcolor: '#f0fdfa', borderRadius: 2, border: '1px solid #99f6e4' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#0f172a', mb: 2 }}>
              Compliance Evidence Statement
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              This report evidences that <strong>{user?.companyName || 'the organization'}</strong> maintains 
              continuous automated monitoring of AI systems for safety, bias, and harmful content using 
              the Verit Compliance platform.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                ✓ <strong>Continuous Monitoring:</strong> {scheduledSequences} automated test sequences running on schedule
              </Typography>
              <Typography variant="body2">
                ✓ <strong>Multi-Layer Analysis:</strong> General Safety, Regulatory Compliance, and User Perception analysis engines active
              </Typography>
              <Typography variant="body2">
                ✓ <strong>Harm Category Coverage:</strong> {chartData.categoryCounts.length} safety categories monitored
              </Typography>
              {apiEvalSummary && apiEvalSummary.total > 0 && (
              <Typography variant="body2">
                ✓ <strong>API Evaluations:</strong> {apiEvalSummary.total} LLM outputs evaluated ({apiEvalSummary.safe} safe, {apiEvalSummary.unsafe} flagged)
              </Typography>
              )}
              <Typography variant="body2">
                ✓ <strong>Alert System:</strong> Continuous automated alerting with {stats.activeAlertsCount} current active alerts
              </Typography>
              <Typography variant="body2">
                ✓ <strong>Overall Safety Score:</strong> {stats.currentSafetyScore.toFixed(1)}% 
                ({stats.currentSafetyScore >= 90 ? 'Excellent' : stats.currentSafetyScore >= 75 ? 'Good' : 'Needs Improvement'})
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 4, pt: 2, borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Generated by Verit Compliance — Technical AI Monitoring & Evidence Platform
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Provides monitoring evidence that may support various AI governance frameworks
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              For questions about this report, contact your organization's AI governance administrator.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#f0fdfa' }}>
          <Button data-ga-label="Report Close" onClick={() => setComplianceReportOpen(false)}>Close</Button>
          <Button 
            data-ga-label="Report Export PDF"
            variant="contained" 
            startIcon={exportingPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
            onClick={handleExportPdf}
            disabled={exportingPdf}
            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152a4a' } }}
          >
            {exportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <SpaceInvadersModal open={spaceInvadersOpen} onClose={() => setSpaceInvadersOpen(false)} />
    </Box>
  );
};

export default Dashboard;
